import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardSection } from '../hooks/useDashboardSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faShoppingCart, faCog, faBars, faMoon, faSun, faTruck,
  faMobileAlt, faCheckCircle, faCheck, faNairaSign, faEye, faFilter, faSearch,
  faUser, faTimes, faHeart, faBell, faHistory, faMapMarked, faStar,
  faExchangeAlt, faShoppingBag, faBox, faChartLine, faSignOutAlt,
  faUserCircle, faQuestionCircle, faShieldAlt, faHome, faWallet, faCrown,
  faPlus, faSpinner, faLock, faComments, faPaperPlane, faUniversity,
  faStore, faTag, faUpload, faSave, faMapMarkerAlt, faTrashAlt, faExclamationTriangle,
  faDownload, faClipboardList, faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';
import { format, subDays } from 'date-fns';
import Toast from './Toast';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import UserActivity from './UserActivity';
import OrderTracking from './OrderTracking';
import { userAPI, buyerAPI, notificationsAPI, walletAPI, savedSearchesAPI, wishlistAPI, swapAPI, badgesAPI, priceAlertsAPI, requestsAPI, paymentAPI } from '../services/api';
import { getStoredDarkMode, setStoredDarkMode } from '../utils/theme';
import { uploadImage } from '../services/imageUploadService';
import { useSwap } from '../context/SwapContext';
import { useAuth } from '../context/AuthContext';
import KYCVerification from './KYCVerification';
import KYCStatusBanner from './KYCStatusBanner';
import EscrowAnalytics from './EscrowAnalytics';
import EscrowSettingsPanel from './EscrowSettingsPanel';
import BankAccountsManager from './BankAccountsManager';
import WalletWithdrawModal from './WalletWithdrawModal';
import AddressManagement from './AddressManagement';
import TwoFactorAuth from './TwoFactorAuth';
import lightLogo from '../assets/autoboy_logo2.png';
import darkLogo from '../assets/autoboy_logo3.png';
import { TableCard, DataTable, THead, Th, Tr, Td, Badge, TableEmpty, IconBtn, statusTone } from './ui/DataTable';
import './BuyerDashboard.css';
import '../assets/css/wallet-modals.css';
import '../assets/css/settings-modern.css';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  // Section lives in the URL (?section=...) so Back returns to the tab
  // you were on — see useDashboardSection.
  const [activeSection, setActiveSection] = useDashboardSection('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredDarkMode());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false);
  const [toast, setToast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Notifications data - now from API
  const [notifications, setNotifications] = useState([]);

  // Chat/Messages data

  // Order tracking state
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [showTracking, setShowTracking] = useState(false);

  const [stats, setStats] = useState({
    activeOrders: 0,
    completedOrders: 0,
    wishlistItems: 0,
    totalSpent: 0
  });

  const [wishlist, setWishlist] = useState([]);
  const [orders, setOrders] = useState([]);

  const [savedSearches, setSavedSearches] = useState([]);

  // Recent activity - now from API
  const [recentActivity, setRecentActivity] = useState([]);

  // Wallet data
  const [walletBalance, setWalletBalance] = useState({
    balance: 0,
    heldBalance: 0
  });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);

  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    orderUpdates: true,
    priceAlerts: true,
    promotions: false,
    newMessages: true,
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordForDeletion, setPasswordForDeletion] = useState('');
  const [exportingData, setExportingData] = useState(false);
  const [settingsTab, setSettingsTab] = useState('profile');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  // Price alerts
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [newAlertProductId, setNewAlertProductId] = useState('');
  const [newAlertTargetPrice, setNewAlertTargetPrice] = useState('');
  const [creatingAlert, setCreatingAlert] = useState(false);

  // Badges
  const [userBadges, setUserBadges] = useState([]);
  const [badgePoints, setBadgePoints] = useState(0);

  // Active sessions
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);

  // Profile settings form state
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', email: '', phone: '', address: '', bio: '', location: '', gender: '', date_of_birth: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Avatar/Profile Picture state
  const [avatarUploadMethod, setAvatarUploadMethod] = useState('url');
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileInputRef = React.useRef(null);

  // Swap Deals state
  const [swapDeals, setSwapDeals] = useState([]);
  const [swapActiveTab, setSwapActiveTab] = useState('active');
  const [isLoadingSwaps, setIsLoadingSwaps] = useState(false);

  // Swap Offers (from SwapContext)
  const { swaps: swapOffers, getInitiatedSwaps, calculateSwapFees, acceptPrice, declinePrice, cancelSwap: cancelSwapOffer, processSwapPayment, fetchSwaps, loading: swapLoading, confirmShipment, confirmDelivery, completeSwap } = useSwap();
  const [wtbRequests, setWtbRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestResponses, setRequestResponses] = useState([]);
  const [viewingResponsesFor, setViewingResponsesFor] = useState(null);
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: '',
    budget_min: '',
    budget_max: '',
    category: '',
    location_preference: '',
    condition_preference: '',
    urgency: ''
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('AutoboyExpress-dash-dark-mode', isDarkMode);
    // Tailwind's compiled `:where(...)` selector for our custom darkMode config
    // only expands `.tw-dark-mode` to also match descendants (`.tw-dark-mode *`);
    // `.tw-AutoboyExpress-dash-dark-mode` compiles as a self-match-only term, so
    // toggling it on <body> never reaches nested `dark:` utilities (see ui/DataTable.jsx).
    // `tw-dark-mode` is the class that actually cascades — ThemeSync strips it on
    // dashboard routes to avoid storefront bleed, so we re-assert it here.
    document.body.classList.toggle('tw-dark-mode', isDarkMode);
    setStoredDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Check for payment success on page load - Verify payment then update wallet
  useEffect(() => {
    const verifyAndUpdateWallet = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const reference = urlParams.get('reference') ||
        urlParams.get('trxref') ||
        localStorage.getItem('pending_payment_reference');

      if (paymentStatus === 'success' || reference) {
        try {
          // Show verifying message
          setToast({ message: '🔄 Verifying payment...', type: 'info' });

          // Switch to wallet section
          setActiveSection('wallet');

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);

          // Verify payment with backend (same as premium upgrade)
          const { paymentAPI } = await import('../services/api');

          const verificationResponse = await paymentAPI.verifyPayment(reference);

          if (verificationResponse && verificationResponse.success) {
            const paymentData = verificationResponse.data;

            // Check if payment is for wallet top-up
            if (paymentData.payment_type === 'wallet_topup') {
              // Fetch updated wallet balance
              const walletResponse = await walletAPI.getBalance();

              const vwPayload = walletResponse?.data || walletResponse;
              if (vwPayload) {
                const balanceData = vwPayload.balance !== undefined ? vwPayload : (vwPayload.data || vwPayload);

                const newBalance = parseFloat(balanceData.balance) || 0;
                const newHeldBalance = parseFloat(balanceData.held_balance || balanceData.heldBalance) || 0;

                setWalletBalance({
                  balance: newBalance,
                  heldBalance: newHeldBalance
                });

                // Show credit notification with amount
                const topupAmount = parseFloat(paymentData.amount) || 0;
                setToast({
                  message: `💰 Wallet Credited: +₦${topupAmount.toLocaleString()} | New Balance: ₦${newBalance.toLocaleString()}`,
                  type: 'success'
                });
              }

              // Clear pending payment data
              localStorage.removeItem('pending_payment_reference');
              localStorage.removeItem('pending_payment_id');

              // Refresh transactions
              fetchWalletTransactions();
            } else {
              setToast({ message: '✓ Payment verified successfully!', type: 'success' });
            }
          } else {
            setToast({
              message: verificationResponse?.message || 'Payment verification failed. Please contact support.',
              type: 'error'
            });
          }
        } catch (error) {

          // Clear failed payment references
          localStorage.removeItem('pending_payment_reference');
          localStorage.removeItem('pending_payment_id');

          // Only show error toast if it's not a "failed" status payment
          if (!error.message?.includes('Payment status: failed')) {
            setToast({
              message: error.message || 'Error verifying payment. Please contact support.',
              type: 'error'
            });
          }
        }
      }
    };

    verifyAndUpdateWallet();
  }, []);

  // Fetch all buyer data
  useEffect(() => {
    const fetchBuyerData = async () => {
      try {
        setIsLoading(true);

        // Fetch user profile (handle errors gracefully)
        try {
          const profileResponse = await userAPI.getProfile();
          const profilePayload = profileResponse?.data || profileResponse;
          if (profilePayload) {
            setUserProfile(profilePayload);
          }
        } catch (error) {
        }

        // Fetch buyer dashboard stats from dedicated endpoint (handle errors gracefully)
        try {
          const dashboardResponse = await buyerAPI.getDashboard();
          const dashPayload = dashboardResponse?.data || dashboardResponse;
          if (dashPayload) {
            setStats({
              activeOrders: dashPayload.active_orders || 0,
              completedOrders: dashPayload.completed_orders || 0,
              wishlistItems: dashPayload.wishlist_count || 0,
              totalSpent: dashPayload.total_spent || 0
            });
          }
        } catch (error) {
          // Keep default stats values
        }

        // Fetch orders for order history display (handle errors gracefully)
        try {
          const ordersResponse = await userAPI.getOrders();
          const ordPayload = ordersResponse?.data || ordersResponse;
          if (ordPayload) {
            const raw = ordPayload.orders || ordPayload.data?.orders || (Array.isArray(ordPayload) ? ordPayload : []);
            setOrders(raw);
          }
        } catch (error) {
          setOrders([]);
        }

        // Fetch wishlist for wishlist display (handle errors gracefully)
        try {
          const wishlistResponse = await wishlistAPI.getWishlist();
          const wlPayload = wishlistResponse?.data || wishlistResponse;
          if (wlPayload) {
            const wishlistData = wlPayload.items || (Array.isArray(wlPayload) ? wlPayload : []);
            setWishlist(wishlistData);
          }
        } catch (error) {
          setWishlist([]);
        }

        // Fetch notifications (handle errors gracefully)
        try {
          const notificationsResponse = await notificationsAPI.getNotifications();
          const notifPayload = notificationsResponse?.data || notificationsResponse;
          if (notifPayload) {
            const notifList = notifPayload.notifications || (Array.isArray(notifPayload) ? notifPayload : []);
            setNotifications(notifList.slice(0, 10)); // Show last 10
          }
        } catch (error) {
          setNotifications([]);
        }


        // Fetch recent activity (handle errors gracefully)
        try {
          const activityResponse = await buyerAPI.getRecentActivity();
          if (activityResponse) {
            const d = activityResponse.data || activityResponse;
            // Handle multiple shapes: { activities: [] }, { orders: [] }, { data: [] }, or plain array
            const rawItems =
              d?.activities ||
              d?.data?.activities ||
              d?.orders ||
              d?.data?.orders ||
              d?.data ||
              (Array.isArray(d) ? d : []);
            const activityList = rawItems.map((item, idx) => ({
              id: item._id || item.id || idx,
              action: item.action || item.title || getActivityAction(item.status),
              item: item.product_title || item.product_name || item.description || item.message || 'Activity',
              time: formatActivityTime(item.created_at || item.timestamp || item.updated_at),
              icon: getActivityIcon(item.type || item.status)
            }));
            setRecentActivity(activityList.slice(0, 10));
          }
        } catch (error) {
          setRecentActivity([]);
        }

        // Fetch wallet balance with enhanced parsing
        const walletResponse = await walletAPI.getBalance();

        const walPayload = walletResponse?.data || walletResponse;
        if (walPayload) {
          const balanceData = walPayload.balance !== undefined ? walPayload : (walPayload.data || walPayload);

          const newBalance = parseFloat(balanceData.balance) || 0;
          const newHeldBalance = parseFloat(balanceData.held_balance || balanceData.heldBalance) || 0;

          setWalletBalance({
            balance: newBalance,
            heldBalance: newHeldBalance
          });
        }

        // Fetch saved searches (handle CORS and other errors gracefully)
        try {
          const savedSearchesResponse = await savedSearchesAPI.getSavedSearches();
          const ssPayload = savedSearchesResponse?.data || savedSearchesResponse;
          if (ssPayload) {
            setSavedSearches(Array.isArray(ssPayload) ? ssPayload.slice(0, 5) : []);
          }
        } catch (error) {
          setSavedSearches([]);
        }

      } catch (error) {
        // Graceful degradation - continue with empty/default data
      } finally {
        setIsLoading(false);
      }
    };

    fetchBuyerData();
  }, []);

  // Sync avatar preview with userProfile (avatar can be at top-level or inside profile)
  useEffect(() => {
    const avatar = userProfile?.avatar || userProfile?.profile?.avatar;
    if (avatar) {
      setAvatarPreview(avatar);
    }
  }, [userProfile]);

  // Sync profile form with userProfile when it loads
  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        firstName: userProfile.profile?.first_name || '',
        lastName: userProfile.profile?.last_name || '',
        email: userProfile.email || userProfile.profile?.email || '',
        phone: userProfile.phone || userProfile.profile?.phone || '',
        address: userProfile.profile?.address || userProfile.profile?.delivery_address || '',
        bio: userProfile.profile?.bio || '',
        location: userProfile.profile?.location || '',
        gender: userProfile.profile?.gender || '',
        date_of_birth: userProfile.profile?.date_of_birth || '',
      });
    }
  }, [userProfile]);

  // Load notification preferences
  useEffect(() => {
    const loadNotifPrefs = async () => {
      try {
        const res = await notificationsAPI.getPreferences();
        const data = res?.data || {};
        setNotificationPrefs({
          emailOrders: data.email_orders ?? data.emailOrders ?? true,
          smsDelivery: data.sms_delivery ?? data.smsDelivery ?? true,
          priceDrops: data.price_drops ?? data.priceDrops ?? false,
          recommendations: data.recommendations ?? true,
        });
      } catch {
        // keep defaults on error
      }
    };
    loadNotifPrefs();
  }, []);

  const handleSaveNotifPrefs = async () => {
    try {
      setSavingNotifPrefs(true);
      await notificationsAPI.updatePreferences({
        email_notifications: notificationPrefs.emailNotifications,
        push_notifications: notificationPrefs.pushNotifications,
        sms_notifications: notificationPrefs.smsNotifications,
        order_updates: notificationPrefs.orderUpdates,
        price_alerts: notificationPrefs.priceAlerts,
        promotions: notificationPrefs.promotions,
        new_messages: notificationPrefs.newMessages,
      });
      setToast({ message: 'Notification preferences saved.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to save notification preferences.', type: 'error' });
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({ message: 'New passwords do not match.', type: 'error' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setToast({ message: 'New password must be at least 6 characters.', type: 'error' });
      return;
    }
    try {
      setSavingPassword(true);
      await userAPI.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setToast({ message: 'Password changed successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err?.message || 'Failed to change password.', type: 'error' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      await userAPI.updateProfile({
        first_name: profileForm.firstName,
        last_name: profileForm.lastName,
        email: profileForm.email,
        phone: profileForm.phone,
        address: profileForm.address,
        bio: profileForm.bio,
        location: profileForm.location,
        gender: profileForm.gender,
        date_of_birth: profileForm.date_of_birth,
        profile: {
          first_name: profileForm.firstName,
          last_name: profileForm.lastName,
          phone: profileForm.phone,
          address: profileForm.address,
          delivery_address: profileForm.address,
          bio: profileForm.bio,
          location: profileForm.location,
          gender: profileForm.gender,
          date_of_birth: profileForm.date_of_birth,
        }
      });
      // Update cached user profile
      setUserProfile(prev => prev ? {
        ...prev,
        email: profileForm.email,
        phone: profileForm.phone,
        profile: {
          ...(prev.profile || {}),
          first_name: profileForm.firstName,
          last_name: profileForm.lastName,
          phone: profileForm.phone,
          address: profileForm.address,
          delivery_address: profileForm.address,
        }
      } : prev);
      setToast({ message: 'Profile updated successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err?.message || 'Failed to save profile.', type: 'error' });
    } finally {
      setSavingProfile(false);
    }
  };

  // Load price alerts
  useEffect(() => {
    priceAlertsAPI.getPriceAlerts()
      .then(res => setPriceAlerts(Array.isArray(res?.data) ? res.data : []))
      .catch(() => { });
  }, []);

  const handleCreatePriceAlert = async () => {
    if (!newAlertProductId.trim() || !newAlertTargetPrice) return;
    try {
      setCreatingAlert(true);
      await priceAlertsAPI.createPriceAlert(newAlertProductId.trim(), parseFloat(newAlertTargetPrice));
      setToast({ message: 'Price alert set!', type: 'success' });
      setNewAlertProductId(''); setNewAlertTargetPrice('');
      const res = await priceAlertsAPI.getPriceAlerts();
      setPriceAlerts(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setToast({ message: err?.response?.data?.error || 'Failed to create price alert.', type: 'error' });
    } finally { setCreatingAlert(false); }
  };

  const handleDeletePriceAlert = async (alertId) => {
    try {
      await priceAlertsAPI.deletePriceAlert(alertId);
      setPriceAlerts(prev => prev.filter(a => (a.id || a._id) !== alertId));
    } catch { setToast({ message: 'Failed to remove alert.', type: 'error' }); }
  };

  // Load badges on mount
  useEffect(() => {
    Promise.all([
      badgesAPI.getUserBadges().catch(() => ({ data: null })),
      badgesAPI.getUserRewards().catch(() => ({ data: null })),
    ]).then(([badgesRes, rewardsRes]) => {
      setUserBadges(badgesRes?.data?.badges || []);
      setBadgePoints(rewardsRes?.data?.total_points || rewardsRes?.data?.points || 0);
    });
  }, []);

  // Load sessions when settings section active
  useEffect(() => {
    if (activeSection === 'settings') {
      setSessionsLoading(true);
      userAPI.getActiveSessions()
        .then(res => setActiveSessions(Array.isArray(res?.data?.sessions || res?.data) ? (res?.data?.sessions || res?.data) : []))
        .catch(() => setActiveSessions([]))
        .finally(() => setSessionsLoading(false));
    }
  }, [activeSection]);

  const handleTerminateSession = async (sessionId) => {
    try {
      setTerminatingSession(sessionId);
      await userAPI.terminateSession(sessionId);
      setActiveSessions(prev => prev.filter(s => (s.id || s._id) !== sessionId));
      setToast({ message: 'Session terminated.', type: 'success' });
    } catch { setToast({ message: 'Failed to terminate session.', type: 'error' }); }
    finally { setTerminatingSession(null); }
  };

  const handleTerminateAllSessions = async () => {
    if (!window.confirm('Sign out of all other devices?')) return;
    try {
      await userAPI.terminateAllOtherSessions();
      const res = await userAPI.getActiveSessions();
      setActiveSessions(Array.isArray(res?.data?.sessions || res?.data) ? (res?.data?.sessions || res?.data) : []);
      setToast({ message: 'All other sessions terminated.', type: 'success' });
    } catch { setToast({ message: 'Failed to terminate sessions.', type: 'error' }); }
  };

  const handleExportData = async () => {
    try {
      setExportingData(true);
      const response = await userAPI.exportUserData();
      if (response && response.success) {
        setToast({ message: 'Data export initialized. Check your email.', type: 'success' });
      } else {
        setToast({ message: 'Failed to initialize data export.', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Error exporting data.', type: 'error' });
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!passwordForDeletion) {
      setToast({ message: 'Enter password to confirm.', type: 'error' });
      return;
    }

    if (!window.confirm('Irreversible action. Proceed with account deletion?')) return;

    try {
      setDeletingAccount(true);
      const response = await userAPI.deleteAccount(passwordForDeletion);
      if (response && response.success) {
        setToast({ message: 'Account deleted.', type: 'success' });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/';
        }, 3000);
      } else {
        setToast({ message: response?.message || 'Failed to delete account.', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Error deleting account.', type: 'error' });
    } finally {
      setDeletingAccount(false);
    }
  };

  const fetchWtbRequests = async () => {
    try {
      setLoadingRequests(true);
      const res = await requestsAPI.getMyRequests();
      setWtbRequests(Array.isArray(res?.data) ? res?.data : (res?.data?.requests || []));
    } catch (err) {
      setToast({ message: 'Failed to fetch your requests.', type: 'error' });
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      setSubmittingRequest(true);
      const response = await requestsAPI.createRequest({
        ...newRequest,
        budget_min: newRequest.budget_min ? parseFloat(newRequest.budget_min) : undefined,
        budget_max: newRequest.budget_max ? parseFloat(newRequest.budget_max) : undefined
      });
      if (response && response.success) {
        setToast({ message: 'Request posted successfully!', type: 'success' });
        setShowRequestModal(false);
        setNewRequest({ title: '', description: '', budget_min: '', budget_max: '', category: '', location_preference: '', condition_preference: '', urgency: '' });
        fetchWtbRequests();
      }
    } catch (err) {
      setToast({ message: err?.response?.data?.error || 'Failed to post request.', type: 'error' });
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this search request?')) return;
    try {
      await requestsAPI.deleteRequest(requestId);
      setWtbRequests(prev => prev.filter(r => (r.id || r._id) !== requestId));
      setToast({ message: 'Request deleted.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to delete request.', type: 'error' });
    }
  };

  const handleMarkRequestFulfilled = async (requestId) => {
    try {
      await requestsAPI.fulfillRequest(requestId);
      setToast({ message: 'Request marked as fulfilled!', type: 'success' });
      fetchWtbRequests();
    } catch {
      setToast({ message: 'Failed to update request.', type: 'error' });
    }
  };

  const fetchRequestResponses = async (requestId) => {
    try {
      setViewingResponsesFor(requestId);
      const res = await requestsAPI.getRequestResponses(requestId);
      setRequestResponses(Array.isArray(res?.data?.responses) ? res?.data?.responses : []);
    } catch {
      setToast({ message: 'Failed to load seller responses.', type: 'error' });
    }
  };

  // Helper functions for activity mapping
  const getActivityAction = (status) => {
    const actions = {
      pending: 'Ordered',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Received delivery',
      cancelled: 'Cancelled'
    };
    return actions[status] || 'Ordered';
  };

  const getActivityIcon = (statusOrType) => {
    const icons = {
      pending: faShoppingCart,
      processing: faBox,
      shipped: faTruck,
      delivered: faCheckCircle,
      cancelled: faTimes,
      // activity type values
      order: faShoppingCart,
      purchase: faShoppingCart,
      payment: faNairaSign,
      wishlist: faHeart,
      review: faStar,
      login: faUser,
      swap: faExchangeAlt,
    };
    return icons[statusOrType] || faShoppingCart;
  };

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return format(date, 'MMM d');
  };

  // Fetch WTB requests when requests section is active
  useEffect(() => {
    if (activeSection === 'requests') {
      fetchWtbRequests();
    }
  }, [activeSection]);

  // Fetch wallet balance and transactions when wallet section is active
  useEffect(() => {
    if (activeSection === 'wallet') {
      fetchWalletBalance();
      fetchWalletTransactions();
    }
  }, [activeSection]);

  // Fetch wishlist when wishlist section is active
  useEffect(() => {
    const fetchWishlistData = async () => {
      if (activeSection === 'wishlist') {
        try {
          const wishlistResponse = await wishlistAPI.getWishlist();
          const wlRefPayload = wishlistResponse?.data || wishlistResponse;
          if (wlRefPayload) {
            const wishlistData = wlRefPayload.items || (Array.isArray(wlRefPayload) ? wlRefPayload : []);
            setWishlist(wishlistData);
          }
        } catch (error) {
          setWishlist([]);
        }
      }
    };

    fetchWishlistData();
  }, [activeSection]);

  // Refresh swap data from SwapContext when swap sections become active
  useEffect(() => {
    if (activeSection === 'swap-deals' || activeSection === 'swap-offers') {
      fetchSwaps();
    }
  }, [activeSection]);

  // Handle navigation from other pages
  useEffect(() => {
    // useDashboardSection already seeds from location.state on mount; the
    // replaceState({}) that used to live here wiped the section out of the
    // history entry, so a later Back could never restore it.
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);
    }
  }, [location]);

  const fetchWalletBalance = async () => {
    try {
      const response = await walletAPI.getBalance();

      const fwbPayload = response?.data || response;
      if (fwbPayload) {
        const balanceData = fwbPayload.balance !== undefined ? fwbPayload : (fwbPayload.data || fwbPayload);

        const newBalance = parseFloat(balanceData.balance) || 0;
        const newHeldBalance = parseFloat(balanceData.held_balance || balanceData.heldBalance) || 0;

        setWalletBalance({
          balance: newBalance,
          heldBalance: newHeldBalance
        });

        setToast({
          message: `✓ Wallet balance refreshed: ₦${newBalance.toLocaleString()}`,
          type: 'success'
        });
      }
    } catch (error) {
      setToast({ message: 'Failed to refresh wallet balance', type: 'error' });
    }
  };

  const fetchWalletTransactions = async () => {
    setIsLoadingTransactions(true);
    try {
      const response = await walletAPI.getTransactions({ limit: 20 });

      const txPayload = response?.data || response;
      if (txPayload) {
        let transactions = [];
        if (Array.isArray(txPayload)) {
          transactions = txPayload;
        } else if (Array.isArray(txPayload.transactions)) {
          transactions = txPayload.transactions;
        } else if (txPayload.data && Array.isArray(txPayload.data)) {
          transactions = txPayload.data;
        }

        setWalletTransactions(transactions);
      } else {
        setWalletTransactions([]);
      }
    } catch (error) {
      setToast({ message: 'Failed to load transactions', type: 'error' });
      setWalletTransactions([]);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
    }
  };

  const handleWalletTopup = async () => {
    const amount = parseFloat(topupAmount);

    if (!amount || amount < 100) {
      setToast({ message: 'Minimum top-up amount is ₦100', type: 'error' });
      return;
    }

    try {
      setIsProcessingTopup(true);

      // Import paymentAPI at the top if not already imported
      const { paymentAPI } = await import('../services/api');

      // Initialize payment for wallet topup
      const paymentData = {
        amount: parseInt(amount),
        payment_type: 'wallet_topup',
        currency: 'NGN',
        callback_url: `${window.location.origin}/buyer-dashboard?payment=success`,
        metadata: {
          topup_amount: parseInt(amount)
        }
      };

      const paymentResponse = await paymentAPI.initializePayment(paymentData);

      if (paymentResponse && paymentResponse.data && paymentResponse.data.checkout_url) {
        // Store payment info
        localStorage.setItem('pending_payment_id', paymentResponse.data.payment_id);
        localStorage.setItem('pending_payment_reference', paymentResponse.data.reference);

        // Redirect to Paystack
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        setToast({ message: 'Failed to initialize payment', type: 'error' });
      }
    } catch (error) {
      setToast({ message: error.message || 'Failed to process top-up', type: 'error' });
    } finally {
      setIsProcessingTopup(false);
    }
  };

  const toggleTheme = () => setIsDarkMode(!isDarkMode);
  const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);

  const formatCurrency = (amount) => `₦${amount.toLocaleString()}`;

  // Safe date formatter — avoids date-fns throwing on undefined/invalid dates
  const formatOrderDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try { return format(new Date(dateStr), 'MMM dd, yyyy'); } catch { return 'N/A'; }
  };

  // Normalize an order object from API to display fields
  const getOrderDisplay = (order) => ({
    id: order.order_number || order._id || order.id || '—',
    product: order.product_title || order.items?.[0]?.product_name || order.items?.[0]?.name || order.product || 'Product',
    seller: order.seller_premium
      ? (order.seller_name || order.items?.[0]?.seller_name || order.seller || 'N/A')
      : `Seller ${String(order.seller_id || '000').slice(-3).padStart(3, '0')}`,
    amount: order.total || order.total_amount || order.amount || 0,
    date: formatOrderDate(order.created_at || order.date),
    status: order.status || 'pending',
    paymentStatus: order.payment_status || order.paymentStatus || 'pending',
    rawId: order._id || order.id,
    useEscrow: order.use_escrow,
  });

  const getStatusColor = (status) => {
    const colors = { processing: '#f59e0b', shipped: '#3b82f6', delivered: '#22c55e', cancelled: '#ef4444' };
    return colors[status] || '#6b7280';
  };

  const getStatusText = (status) => {
    const texts = { processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled' };
    return texts[status] || status;
  };

  // Payment Status Helper Functions (matching mobile app and seller dashboards)
  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      pending: '#f59e0b',
      paid: '#22c55e',
      completed: '#22c55e',
      escrow: '#3b82f6',
      failed: '#ef4444',
      refunded: '#6b7280',
      cancelled: '#9ca3af'
    };
    return colors[paymentStatus] || '#6b7280';
  };

  // `completed` covers two very different situations: a plain card/transfer
  // order that simply finished, and an escrow order whose funds the buyer
  // has now released to the seller. Sellers were told the second case still
  // read "In Escrow" (the release path never updated payment_status — fixed
  // backend-side), and once it does update, "Completed" alone doesn't say
  // the money actually moved. Pass the order and escrow-released orders read
  // "Released".
  const getPaymentStatusText = (paymentStatus, order) => {
    const texts = {
      pending: 'Pending',
      paid: 'Paid',
      completed: 'Completed',
      escrow: 'In Escrow',
      failed: 'Failed',
      refunded: 'Refunded',
      cancelled: 'Cancelled'
    };
    const key = String(paymentStatus || '').toLowerCase();
    if (key === 'completed' && (order?.use_escrow || order?.useEscrow)) return 'Released';
    return texts[key] || paymentStatus;
  };

  // Avatar Upload Handlers
  const handleAvatarFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setToast({ message: 'Please select an image file', type: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setToast({ message: 'Image size should be less than 5MB', type: 'error' });
        return;
      }
      setSelectedAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    try {
      setUploadingAvatar(true);
      let avatarUrl = '';

      if (avatarUploadMethod === 'url') {
        avatarUrl = userProfile?.avatar || '';
        if (!avatarUrl || !avatarUrl.startsWith('http')) {
          setToast({ message: 'Please enter a valid image URL', type: 'error' });
          setUploadingAvatar(false);
          return;
        }
      } else {
        if (!selectedAvatarFile) {
          setToast({ message: 'Please select an image file', type: 'error' });
          setUploadingAvatar(false);
          return;
        }
        // Use uploadImage which falls back to ImgBB → Imgur if Cloudinary fails
        const { url } = await uploadImage(selectedAvatarFile);
        avatarUrl = url;
      }

      // Include all existing profile fields — server uses PUT (full replace), sending only
      // avatar would wipe first_name, last_name, bio, etc.
      await userAPI.updateProfile({
        first_name: profileForm.firstName || userProfile?.profile?.first_name || '',
        last_name: profileForm.lastName || userProfile?.profile?.last_name || '',
        bio: profileForm.bio || userProfile?.profile?.bio || '',
        location: profileForm.location || userProfile?.profile?.location || '',
        gender: profileForm.gender || userProfile?.profile?.gender || '',
        date_of_birth: profileForm.date_of_birth || userProfile?.profile?.date_of_birth || '',
        avatar: avatarUrl,
      });

      const updatedProfile = {
        ...userProfile,
        avatar: avatarUrl,
        profile: { ...(userProfile?.profile || {}), avatar: avatarUrl },
      };
      setUserProfile(updatedProfile);

      const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedCache = {
        ...cachedUser,
        avatar: avatarUrl,
        profile: { ...(cachedUser.profile || {}), avatar: avatarUrl },
      };
      localStorage.setItem('user', JSON.stringify(updatedCache));

      setAvatarPreview(avatarUrl);
      setSelectedAvatarFile(null);
      // Notify Navbar/AuthContext so avatar updates live
      window.dispatchEvent(new Event('userUpdated'));
      setToast({ message: 'Profile picture updated successfully!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update profile picture. Please try again.', type: 'error' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const renderDashboardContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* KYC Status Banner */}
      <KYCStatusBanner onNavigateToKYC={() => setActiveSection('kyc')} />

      <div className="AutoboyExpress-dash-quick-actions">
        <button className="AutoboyExpress-dash-quick-action" onClick={() => setActiveSection('orders')}>
          <div className="AutoboyExpress-dash-quick-action-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
          <div><h3>My Orders</h3><p>Track your purchases</p></div>
        </button>
        <button className="AutoboyExpress-dash-quick-action" onClick={() => setActiveSection('wishlist')}>
          <div className="AutoboyExpress-dash-quick-action-icon"><FontAwesomeIcon icon={faHeart} /></div>
          <div><h3>Wishlist</h3><p>Saved items</p></div>
        </button>
        <button className="AutoboyExpress-dash-quick-action" onClick={() => setActiveSection('activity')}>
          <div className="AutoboyExpress-dash-quick-action-icon"><FontAwesomeIcon icon={faHistory} /></div>
          <div><h3>Activity</h3><p>Recent actions</p></div>
        </button>
        <button
          className="AutoboyExpress-dash-quick-action AutoboyExpress-premium-action"
          onClick={() => setShowPremiumUpgrade(true)}
          style={{
            background: 'linear-gradient(135deg, #22C55E, #16A34A)',
            border: '2px solid #FFD700',
            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)'
          }}
        >
          <div className="AutoboyExpress-dash-quick-action-icon" style={{ color: '#FFD700' }}>
            <FontAwesomeIcon icon={faCrown} />
          </div>
          <div>
            <h3 style={{ color: 'white' }}>Go Premium</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Get exclusive deals</p>
          </div>
        </button>
      </div>

      <div className="AutoboyExpress-dash-stats">
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faShoppingCart} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">{stats.activeOrders}</div>
          <div className="AutoboyExpress-dash-stat-label">Active Orders</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faCheckCircle} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">{stats.completedOrders}</div>
          <div className="AutoboyExpress-dash-stat-label">Completed</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faHeart} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">{stats.wishlistItems}</div>
          <div className="AutoboyExpress-dash-stat-label">Wishlist Items</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faNairaSign} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">{formatCurrency(stats.totalSpent)}</div>
          <div className="AutoboyExpress-dash-stat-label">Total Spent</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card AutoboyExpress-wallet-stat-card" style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: 'white' }}>
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon" style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
              <FontAwesomeIcon icon={faWallet} />
            </div>
          </div>
          <div className="AutoboyExpress-wallet-balance-amount">{formatCurrency(walletBalance.balance)}</div>
          <div className="AutoboyExpress-dash-stat-label" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Wallet Balance</div>
          <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.9 }}>
            Refunds & rewards
          </p>
        </div>
      </div>

      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Recent Orders</h2></div>
          <div className="AutoboyExpress-dash-section-content">
            {orders.length > 0 ? (
              orders.slice(0, 3).map(order => {
                const o = getOrderDisplay(order);
                return (
                  <div key={o.rawId || o.id} className="AutoboyExpress-dash-order-item">
                    <div className="AutoboyExpress-dash-order-info">
                      <h4>{o.product}</h4>
                      <p>{o.seller}</p>
                      <span className="AutoboyExpress-dash-order-id">#{o.id}</span>
                    </div>
                    <div className="AutoboyExpress-dash-order-details">
                      <div className="AutoboyExpress-dash-order-amount">{formatCurrency(o.amount)}</div>
                      <div className="AutoboyExpress-dash-order-status" style={{ color: getStatusColor(o.status) }}>
                        {getStatusText(o.status)}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="AutoboyExpress-dash-empty-state">
                <FontAwesomeIcon icon={faShoppingBag} className="AutoboyExpress-dash-empty-icon" />
                <p className="AutoboyExpress-dash-empty-text">No orders yet</p>
                <small className="AutoboyExpress-dash-empty-subtext">Start shopping to see your orders here</small>
              </div>
            )}
          </div>
        </div>

        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Recent Activity</h2></div>
          <div className="AutoboyExpress-dash-section-content">
            {recentActivity.length > 0 ? (
              recentActivity.map(activity => (
                <div key={activity.id} className="AutoboyExpress-buyer-activity-item">
                  <div className="AutoboyExpress-buyer-activity-icon"><FontAwesomeIcon icon={activity.icon} /></div>
                  <div className="AutoboyExpress-buyer-activity-info">
                    <p><strong>{activity.action}</strong> {activity.item}</p>
                    <span>{activity.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="AutoboyExpress-dash-empty-state">
                <FontAwesomeIcon icon={faChartLine} className="AutoboyExpress-dash-empty-icon" />
                <p className="AutoboyExpress-dash-empty-text">No recent activity</p>
                <small className="AutoboyExpress-dash-empty-subtext">Your recent actions will appear here</small>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrdersContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">My Orders</h2>
          <div className="AutoboyExpress-dash-filters">
            <button className="AutoboyExpress-dash-filter-btn"><FontAwesomeIcon icon={faFilter} /> Filter</button>
            <div className="AutoboyExpress-dash-search">
              <FontAwesomeIcon icon={faSearch} />
              <input type="text" placeholder="Search orders..." />
            </div>
          </div>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <TableCard>
            <DataTable
              columns={[{ width: '12%' }, { width: '20%' }, { width: '14%' }, { width: '12%' }, { width: '12%' }, { width: '11%' }, { width: '11%' }, { width: '8%' }]}
              minWidth="900px"
            >
              <THead>
                <Th>Order ID</Th>
                <Th>Product</Th>
                <Th>Seller</Th>
                <Th align="right">Amount</Th>
                <Th>Date</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th align="center">Actions</Th>
              </THead>
              <tbody>
                {orders.length === 0 ? (
                  <TableEmpty colSpan={8} message="No orders yet" />
                ) : (
                  orders.map(order => {
                    const o = getOrderDisplay(order);
                    return (
                      <Tr key={o.rawId || o.id}>
                        <Td className="tw-font-semibold">#{o.id}</Td>
                        <Td title={o.product}>{o.product}</Td>
                        <Td>{o.seller}</Td>
                        <Td align="right" className="tw-font-semibold tw-tabular-nums">{formatCurrency(o.amount)}</Td>
                        <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">{o.date}</Td>
                        <Td><Badge tone={statusTone(o.status)}>{getStatusText(o.status)}</Badge></Td>
                        <Td><Badge tone={statusTone(o.paymentStatus)}>{getPaymentStatusText(o.paymentStatus, o)}</Badge></Td>
                        <Td align="center">
                          <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5">
                            <IconBtn onClick={() => navigate(`/order-confirmation/${o.rawId}`)} title="View order details">
                              <FontAwesomeIcon icon={faEye} />
                            </IconBtn>
                            <IconBtn
                              onClick={() => { setTrackingOrderId(o.rawId); setShowTracking(true); }}
                              title="Track order"
                            >
                              <FontAwesomeIcon icon={faTruck} />
                            </IconBtn>
                            {o.useEscrow && (
                              <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-500 tw-ml-0.5" title="Protected by escrow" />
                            )}
                          </div>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </tbody>
            </DataTable>
          </TableCard>
        </div>
      </div>
    </div>
  );

  const renderWishlistContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">My Wishlist</h2>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => navigate('/shop')}
          >
            <FontAwesomeIcon icon={faShoppingBag} /> Browse Products
          </button>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          {wishlist.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
              <FontAwesomeIcon icon={faHeart} size="3x" style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>Your wishlist is empty</p>
              <p style={{ fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                Save items you love to buy them later
              </p>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={() => navigate('/shop')}
              >
                <FontAwesomeIcon icon={faShoppingBag} /> Start Shopping
              </button>
            </div>
          ) : (
            <div className="AutoboyExpress-buyer-wishlist-grid">
              {wishlist.map((item, index) => {
                // Extract product data from wishlist item
                const product = item.product || item;
                const productId = product._id || product.id || item.product_id;
                const productName = product.title || product.name || 'Product';

                // Handle image paths - check multiple possible formats
                let productImage;
                if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                  // If images is an array of objects with url property
                  productImage = product.images[0].url || product.images[0];
                } else if (product.image) {
                  productImage = product.image;
                } else {
                  // Fallback to a placeholder
                  productImage = '/placeholder-product.png';
                }

                const productPrice = product.price || 0;
                const productCondition = product.condition || 'New';
                const sellerName = product.seller_premium
                  ? (product.seller_name || product.seller?.name || 'AutoboyExpress Seller')
                  : `Seller ${String(product.seller_id || '000').slice(-3).padStart(3, '0')}`;
                const isAvailable = product.quantity > 0 || product.stock > 0 || true; // Default to available

                return (
                  <div key={productId || index} className="AutoboyExpress-buyer-wishlist-card">
                    <div
                      className="AutoboyExpress-buyer-wishlist-image"
                      onClick={() => navigate(`/product/${productId}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={productImage}
                        alt={productName}
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.target.src = '/placeholder-product.png';
                        }}
                      />
                    </div>
                    <div className="AutoboyExpress-buyer-wishlist-info">
                      <h4
                        onClick={() => navigate(`/product/${productId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {productName}
                      </h4>
                      <p className="AutoboyExpress-buyer-wishlist-seller">
                        <FontAwesomeIcon icon={faStore} style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} />
                        {sellerName}
                      </p>
                      <p className="AutoboyExpress-buyer-wishlist-condition">
                        <FontAwesomeIcon icon={faTag} style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} />
                        {productCondition.replace('_', ' ').toUpperCase()}
                      </p>
                      <div className="AutoboyExpress-buyer-wishlist-price">{formatCurrency(productPrice)}</div>
                      <div className="AutoboyExpress-buyer-wishlist-actions">
                        <button
                          className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                          disabled={!isAvailable}
                          onClick={() => navigate(`/product/${productId}`)}
                          style={{ flex: 1 }}
                        >
                          {isAvailable ? 'View Product' : 'Out of Stock'}
                        </button>
                        <button
                          className="AutoboyExpress-dash-action-btn AutoboyExpress-dash-action-btn-danger"
                          onClick={async () => {
                            try {
                              const { wishlistAPI } = await import('../services/api');
                              await wishlistAPI.removeFromWishlist(productId);
                              // Update local state
                              setWishlist(wishlist.filter((_, i) => i !== index));
                              setToast({ message: 'Removed from wishlist', type: 'success' });
                            } catch (error) {
                              setToast({ message: 'Failed to remove from wishlist', type: 'error' });
                            }
                          }}
                          title="Remove from wishlist"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSavedSearchesContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">Saved Searches</h2>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => navigate('/saved-searches')}
          >
            <FontAwesomeIcon icon={faSearch} /> View All Searches
          </button>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          {savedSearches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
              <FontAwesomeIcon icon={faSearch} size="3x" style={{ opacity: 0.5, marginBottom: '1rem' }} />
              <p>No saved searches yet</p>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={() => navigate('/shop')}
                style={{ marginTop: '1rem' }}
              >
                Start Shopping
              </button>
            </div>
          ) : (
            savedSearches.map(search => {
              const searchId = search._id || search.id;
              const formatDate = (dateString) => {
                if (!dateString) return 'Recently';
                const date = new Date(dateString);
                const now = new Date();
                const diffMs = now - date;
                const diffMins = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMs / 3600000);
                const diffDays = Math.floor(diffMs / 86400000);

                if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
                if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
                return date.toLocaleDateString();
              };

              return (
                <div key={searchId} className="AutoboyExpress-buyer-search-item">
                  <div className="AutoboyExpress-buyer-search-info">
                    <h4><FontAwesomeIcon icon={faSearch} /> {search.query}</h4>
                    <p>
                      {search.result_count !== undefined ? `${search.result_count} results` : 'Saved search'} •
                      Saved {formatDate(search.created_at)}
                    </p>
                  </div>
                  <div className="AutoboyExpress-buyer-search-actions">
                    <button
                      className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                      onClick={() => navigate('/saved-searches')}
                    >
                      View All
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderActivityContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <UserActivity />
    </div>
  );

  // Messages Content (Chat UI)
  const renderWalletContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* Wallet Balance Cards */}
      <div className="AutoboyExpress-dash-stats">
        <div className="AutoboyExpress-dash-stat-card AutoboyExpress-wallet-stat-card" style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: 'white' }}>
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon" style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
              <FontAwesomeIcon icon={faWallet} />
            </div>
          </div>
          <div className="AutoboyExpress-wallet-balance-amount">{formatCurrency(walletBalance.balance)}</div>
          <div className="AutoboyExpress-dash-stat-label" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Available Balance</div>
          <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.9 }}>
            Your wallet balance from refunds and rewards
          </p>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowTopupModal(true)}
            style={{
              marginTop: '1rem',
              width: '100%',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Top Up Wallet
          </button>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowWithdrawModal(true)}
            disabled={!walletBalance.balance}
            style={{
              marginTop: '0.5rem',
              width: '100%',
              background: walletBalance.balance ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.08)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              cursor: walletBalance.balance ? 'pointer' : 'not-allowed',
              opacity: walletBalance.balance ? 1 : 0.6
            }}
          >
            <FontAwesomeIcon icon={faMoneyBillWave} /> Withdraw
          </button>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowBankAccounts(true)}
            style={{
              marginTop: '0.5rem',
              width: '100%',
              background: 'rgba(59, 130, 246, 0.3)',
              color: 'white',
              border: '1px solid rgba(59, 130, 246, 0.5)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <FontAwesomeIcon icon={faUniversity} /> Bank Accounts
          </button>
        </div>
      </div>

      {/* Transaction History */}
      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Transaction History</h2>
            <button
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
              onClick={fetchWalletBalance}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FontAwesomeIcon icon={faHistory} /> Refresh Balance
            </button>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {isLoadingTransactions ? (
              <p>Loading transactions...</p>
            ) : walletTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                <FontAwesomeIcon icon={faWallet} size="3x" style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>No transactions yet</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Your refunds and rewards will appear here
                </p>
              </div>
            ) : (
              <TableCard>
                <DataTable
                  columns={[{ width: '16%' }, { width: '14%' }, { width: '38%' }, { width: '18%' }, { width: '14%' }]}
                  minWidth="680px"
                >
                  <THead>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th align="right">Amount</Th>
                    <Th>Status</Th>
                  </THead>
                  <tbody>
                    {walletTransactions.map((transaction, index) => {
                      const isCredit = ['credit', 'topup', 'refund'].includes(transaction.type);
                      return (
                        <Tr key={transaction.id || index}>
                          <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">
                            {new Date(transaction.created_at || transaction.date).toLocaleDateString()}
                          </Td>
                          <Td>
                            <Badge tone={isCredit ? 'green' : 'amber'}>{transaction.type || 'N/A'}</Badge>
                          </Td>
                          <Td title={transaction.description || 'Transaction'}>
                            {transaction.description || 'Transaction'}
                          </Td>
                          <Td
                            align="right"
                            className={`tw-font-bold tw-tabular-nums ${isCredit ? 'tw-text-green-600 dark:tw-text-green-400' : 'tw-text-red-500'}`}
                          >
                            {isCredit ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount || 0))}
                          </Td>
                          <Td>
                            <Badge tone={statusTone(transaction.status || 'pending')}>
                              {transaction.status || 'pending'}
                            </Badge>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </TableCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPriceAlertsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section">
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.5rem', color: '#22C55E' }} />
            Price Alerts
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          {/* Create alert form */}
          <div style={{
            display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem',
            padding: '1rem', position: 'relative',
            background: isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
            borderRadius: '10px',
            border: '1px solid rgba(34,197,94,0.2)'
          }}>
            <input
              type="text"
              placeholder="Product ID"
              value={newAlertProductId}
              onChange={e => setNewAlertProductId(e.target.value)}
              className="bd-settings-input"
              style={{ flex: '1 1 200px', width: 'auto' }}
            />
            <input
              type="number"
              placeholder="Target price (₦)"
              value={newAlertTargetPrice}
              onChange={e => setNewAlertTargetPrice(e.target.value)}
              min="0"
              className="bd-settings-input"
              style={{ flex: '1 1 150px', width: 'auto' }}
            />
            <button
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
              onClick={handleCreatePriceAlert}
              disabled={creatingAlert || !newAlertProductId.trim() || !newAlertTargetPrice}
            >
              {creatingAlert ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBell} />} Set Alert
            </button>
          </div>
          {/* Alert list */}
          {priceAlerts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {priceAlerts.map(alert => {
                const alertId = alert.id || alert._id;
                const productTitle = alert.product?.title || alert.product_name || `Product #${alertId}`;
                const productImage = alert.product?.image;
                const isTriggered = alert.status === 'triggered';
                const isExpired = alert.status === 'expired';
                const statusColor = isTriggered ? '#22c55e' : isExpired ? '#9ca3af' : '#f59e0b';
                const statusLabel = isTriggered ? '✓ Target Reached' : isExpired ? 'Expired' : 'Watching';
                return (
                  <div key={alertId} style={{
                    display: 'flex', alignItems: 'center', gap: '0.9rem',
                    padding: '1rem', borderRadius: '12px',
                    background: isDarkMode ? '#1F2937' : '#F9FAFB',
                    border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`
                  }}>
                    {productImage && (
                      <img src={productImage} alt={productTitle} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>{productTitle}</p>
                      <p style={{ fontSize: '0.8rem', color: isDarkMode ? '#9CA3AF' : '#6b7280' }}>
                        Current: ₦{Number(alert.current_price || 0).toLocaleString()} &bull; Target: ₦{Number(alert.target_price || 0).toLocaleString()}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.78rem', color: statusColor, fontWeight: 600, whiteSpace: 'nowrap' }}>{statusLabel}</span>
                      <button
                        onClick={() => handleDeletePriceAlert(alertId)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.3rem', display: 'flex', alignItems: 'center' }}
                        title="Remove alert"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <FontAwesomeIcon icon={faBell} size="3x" style={{ color: '#22C55E', marginBottom: '1rem', opacity: 0.4 }} />
              <p style={{ fontWeight: 600, color: isDarkMode ? '#F9FAFB' : '#1F2937', marginBottom: '0.5rem' }}>No price alerts yet</p>
              <p style={{ fontSize: '0.875rem', color: isDarkMode ? '#9CA3AF' : '#6b7280' }}>Enter a product ID and target price above to get notified when the price drops.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderBadgesContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section">
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title"><FontAwesomeIcon icon={faStar} style={{ marginRight: '0.5rem', color: '#F59E0B' }} />Badges & Achievements</h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <div style={{ textAlign: 'center', padding: '1rem 0 1.5rem', borderBottom: '1px solid #f0f0f0', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#22C55E', lineHeight: 1 }}>{badgePoints}</div>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.3rem' }}>Reward Points</p>
          </div>
          {userBadges.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }}>
              {userBadges.map((b, idx) => (
                <div key={b.badge?.id || idx} style={{ textAlign: 'center', padding: '1.25rem 1rem', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#fafafa' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{b.badge?.icon || '🏆'}</div>
                  <p style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{b.badge?.name || 'Badge'}</p>
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{b.badge?.description || ''}</p>
                  {b.earned_at && <p style={{ fontSize: '0.7rem', color: '#22C55E', marginTop: '0.5rem' }}>Earned {new Date(b.earned_at).toLocaleDateString()}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', opacity: 0.6 }}>
              <FontAwesomeIcon icon={faStar} size="3x" style={{ color: '#F59E0B', marginBottom: '1rem' }} />
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>No badges yet</p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Keep shopping to earn badges and rewards!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSettingsContent = () => {
    const settingsNavItems = [
      { id: 'profile',       icon: faUser,                label: 'Profile' },
      { id: 'security',      icon: faLock,                label: 'Security' },
      { id: 'notifications', icon: faBell,                label: 'Notifications' },
      { id: 'sessions',      icon: faShieldAlt,           label: 'Sessions' },
      { id: 'account',       icon: faExclamationTriangle, label: 'Account' },
    ];
    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="bd-settings-layout">
          {/* Sidebar Nav */}
          <nav className="bd-settings-nav">
            {settingsNavItems.map(item => (
              <button
                key={item.id}
                className={`bd-settings-nav-item${settingsTab === item.id ? ' active' : ''}`}
                onClick={() => setSettingsTab(item.id)}
              >
                <FontAwesomeIcon icon={item.icon} className="bd-settings-nav-icon" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Panel */}
          <div className="bd-settings-panel">

            {/* PROFILE */}
            {settingsTab === 'profile' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faUser} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Personal Information</h3>
                    <p className="bd-settings-card-sub">Update your name, contact details and address</p>
                  </div>
                </div>
                {/* Avatar */}
                <div className="bd-settings-avatar-row">
                  <div className="bd-settings-avatar-wrap">
                    <img
                      src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.profile?.first_name || 'U')}+${encodeURIComponent(userProfile?.profile?.last_name || 'ser')}&background=22C55E&color=fff&size=256`}
                      alt="Avatar"
                      className="bd-settings-avatar-img"
                      onError={e => { e.target.src = 'https://ui-avatars.com/api/?name=User&background=22C55E&color=fff&size=256'; }}
                    />
                  </div>
                  <div className="bd-settings-avatar-controls">
                    <div className="bd-settings-avatar-toggle">
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'url' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('url')}>URL</button>
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'file' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('file')}>Upload</button>
                    </div>
                    {avatarUploadMethod === 'url' ? (
                      <input type="url" className="bd-settings-input" value={userProfile?.avatar || ''} onChange={e => { setUserProfile(p => ({ ...p, avatar: e.target.value })); setAvatarPreview(e.target.value); }} placeholder="https://example.com/photo.jpg" />
                    ) : (
                      <>
                        <input ref={avatarFileInputRef} type="file" accept="image/*" onChange={handleAvatarFileSelect} style={{ display: 'none' }} />
                        <button type="button" className="bd-settings-btn-outline" onClick={() => avatarFileInputRef.current?.click()}>
                          <FontAwesomeIcon icon={faUpload} /> {selectedAvatarFile ? selectedAvatarFile.name : 'Choose file'}
                        </button>
                      </>
                    )}
                    <button type="button" className="bd-settings-btn-primary" onClick={handleAvatarUpload} disabled={uploadingAvatar}>
                      {uploadingAvatar ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faSave} /> Save Photo</>}
                    </button>
                  </div>
                </div>
                {/* Form */}
                <form onSubmit={handleSaveProfile} className="bd-settings-form">
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>First Name</label>
                      <input type="text" className="bd-settings-input" value={profileForm.firstName} onChange={e => setProfileForm(p => ({ ...p, firstName: e.target.value }))} placeholder="First name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Last Name</label>
                      <input type="text" className="bd-settings-input" value={profileForm.lastName} onChange={e => setProfileForm(p => ({ ...p, lastName: e.target.value }))} placeholder="Last name" />
                    </div>
                  </div>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>Email <span className="bd-settings-readonly-badge">Read only</span></label>
                      <input type="email" className="bd-settings-input bd-readonly" value={profileForm.email} readOnly />
                    </div>
                    <div className="bd-settings-field">
                      <label>Phone</label>
                      <input type="tel" className="bd-settings-input" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="+234 800 000 0000" />
                    </div>
                  </div>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>Gender</label>
                      <select className="bd-settings-input" value={profileForm.gender} onChange={e => setProfileForm(p => ({ ...p, gender: e.target.value }))}>
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                      </select>
                    </div>
                    <div className="bd-settings-field">
                      <label>Date of Birth</label>
                      <input type="date" className="bd-settings-input" value={profileForm.date_of_birth} onChange={e => setProfileForm(p => ({ ...p, date_of_birth: e.target.value }))} />
                    </div>
                  </div>
                  <div className="bd-settings-field">
                    <label>Location</label>
                    <input type="text" className="bd-settings-input" value={profileForm.location} onChange={e => setProfileForm(p => ({ ...p, location: e.target.value }))} placeholder="City, State (e.g. Lagos, Lagos)" />
                  </div>
                  <div className="bd-settings-field">
                    <label>Delivery Address</label>
                    <textarea className="bd-settings-input" rows="2" value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))} placeholder="Your default delivery address" />
                  </div>
                  <div className="bd-settings-field">
                    <label>Bio</label>
                    <textarea className="bd-settings-input" rows="3" value={profileForm.bio} onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))} placeholder="A short bio about yourself (optional)" />
                  </div>
                  <div className="bd-settings-actions">
                    <button type="submit" className="bd-settings-btn-primary" disabled={savingProfile}>
                      {savingProfile ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faSave} /> Save Changes</>}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* SECURITY */}
            {settingsTab === 'security' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="bd-settings-card">
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faLock} className="bd-settings-card-icon" />
                    <div>
                      <h3 className="bd-settings-card-title">Change Password</h3>
                      <p className="bd-settings-card-sub">Keep your account secure with a strong password</p>
                    </div>
                  </div>
                  <form onSubmit={handleChangePassword} className="bd-settings-form">
                    <div className="bd-settings-field">
                      <label>Current Password</label>
                      <input type="password" className="bd-settings-input" value={passwordForm.currentPassword} onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))} placeholder="Enter current password" required />
                    </div>
                    <div className="bd-settings-grid-2">
                      <div className="bd-settings-field">
                        <label>New Password</label>
                        <input type="password" className="bd-settings-input" value={passwordForm.newPassword} onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))} placeholder="Min. 6 characters" required />
                      </div>
                      <div className="bd-settings-field">
                        <label>Confirm New Password</label>
                        <input type="password" className="bd-settings-input" value={passwordForm.confirmPassword} onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))} placeholder="Re-enter new password" required />
                      </div>
                    </div>
                    <div className="bd-settings-actions">
                      <button type="submit" className="bd-settings-btn-primary" disabled={savingPassword}>
                        {savingPassword ? <><FontAwesomeIcon icon={faSpinner} spin /> Updating…</> : <><FontAwesomeIcon icon={faLock} /> Update Password</>}
                      </button>
                    </div>
                  </form>
                </div>
                <div className="bd-settings-card">
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faShieldAlt} className="bd-settings-card-icon" />
                    <div>
                      <h3 className="bd-settings-card-title">Two-Factor Authentication</h3>
                      <p className="bd-settings-card-sub">Add an extra layer of security to your account</p>
                    </div>
                  </div>
                  <TwoFactorAuth isDarkMode={isDarkMode} />
                </div>
              </div>
            )}

            {/* NOTIFICATIONS */}
            {settingsTab === 'notifications' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faBell} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Notification Preferences</h3>
                    <p className="bd-settings-card-sub">Choose what you want to be notified about</p>
                  </div>
                </div>
                <div className="bd-notif-list">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications',   sub: 'Receive updates via email' },
                    { key: 'pushNotifications',  label: 'Push Notifications',    sub: 'Browser and app push alerts' },
                    { key: 'smsNotifications',   label: 'SMS Notifications',     sub: 'Text messages for urgent updates' },
                    { key: 'orderUpdates',       label: 'Order Updates',         sub: 'Status changes for your orders' },
                    { key: 'priceAlerts',        label: 'Price Drop Alerts',     sub: 'When wishlist items drop in price' },
                    { key: 'promotions',         label: 'Promotions & Offers',   sub: 'Platform deals and promotions' },
                    { key: 'newMessages',        label: 'New Messages',          sub: 'When you receive a new message' },
                  ].map(({ key, label, sub }) => (
                    <div key={key} className="bd-notif-item">
                      <div className="bd-notif-item-text">
                        <span className="bd-notif-item-label">{label}</span>
                        <span className="bd-notif-item-sub">{sub}</span>
                      </div>
                      <label className="bd-toggle">
                        <input type="checkbox" checked={notificationPrefs[key]} onChange={e => setNotificationPrefs(p => ({ ...p, [key]: e.target.checked }))} />
                        <span className="bd-toggle-track"><span className="bd-toggle-thumb" /></span>
                      </label>
                    </div>
                  ))}
                </div>
                <div className="bd-settings-actions">
                  <button className="bd-settings-btn-primary" onClick={handleSaveNotifPrefs} disabled={savingNotifPrefs}>
                    {savingNotifPrefs ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faSave} /> Save Preferences</>}
                  </button>
                </div>
              </div>
            )}

            {/* SESSIONS */}
            {settingsTab === 'sessions' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faShieldAlt} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Active Sessions</h3>
                    <p className="bd-settings-card-sub">Devices currently signed in to your account</p>
                  </div>
                  {activeSessions.length > 1 && (
                    <button className="bd-settings-btn-danger-outline" style={{ marginLeft: 'auto' }} onClick={handleTerminateAllSessions}>
                      Sign Out All Other Devices
                    </button>
                  )}
                </div>
                {sessionsLoading ? (
                  <p className="bd-settings-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading sessions…</p>
                ) : activeSessions.length === 0 ? (
                  <p className="bd-settings-empty">No active sessions found.</p>
                ) : (
                  <div className="bd-session-list">
                    {activeSessions.map((session, idx) => {
                      const sessionId = session.id || session._id || idx;
                      const isCurrent = session.is_current || session.isCurrent;
                      return (
                        <div key={sessionId} className={`bd-session-item${isCurrent ? ' current' : ''}`}>
                          <div className="bd-session-info">
                            <p className="bd-session-device">
                              {session.device_name || session.device || session.browser || 'Unknown Device'}
                              {isCurrent && <span className="bd-session-badge">This device</span>}
                            </p>
                            <p className="bd-session-meta">
                              {session.ip_address || ''}
                              {session.location ? ` · ${session.location}` : ''}
                              {session.last_active ? ` · ${new Date(session.last_active).toLocaleString()}` : ''}
                            </p>
                          </div>
                          {!isCurrent && (
                            <button className="bd-settings-btn-danger-outline" onClick={() => handleTerminateSession(sessionId)} disabled={terminatingSession === sessionId}>
                              {terminatingSession === sessionId ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Sign Out'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ACCOUNT */}
            {settingsTab === 'account' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="bd-settings-card">
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faDownload} className="bd-settings-card-icon" />
                    <div>
                      <h3 className="bd-settings-card-title">Export My Data</h3>
                      <p className="bd-settings-card-sub">Download all your personal data. A copy will be sent to your email.</p>
                    </div>
                  </div>
                  <div className="bd-settings-actions">
                    <button className="bd-settings-btn-outline" onClick={handleExportData} disabled={exportingData}>
                      {exportingData ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing…</> : <><FontAwesomeIcon icon={faDownload} /> Export My Data</>}
                    </button>
                  </div>
                </div>
                <div className="bd-settings-card bd-settings-card-danger">
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="bd-settings-card-icon danger" />
                    <div>
                      <h3 className="bd-settings-card-title danger">Delete Account</h3>
                      <p className="bd-settings-card-sub">Permanently remove your account and all data. This action cannot be undone.</p>
                    </div>
                  </div>
                  {!showDeleteConfirm ? (
                    <div className="bd-settings-actions">
                      <button className="bd-settings-btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete My Account
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleDeleteAccount} className="bd-settings-form">
                      <p className="bd-settings-danger-warning">This will permanently delete your account. Enter your password to confirm.</p>
                      <div className="bd-settings-field">
                        <label>Confirm Password</label>
                        <input type="password" className="bd-settings-input" value={passwordForDeletion} onChange={e => setPasswordForDeletion(e.target.value)} placeholder="Your current password" required />
                      </div>
                      <div className="bd-settings-actions">
                        <button type="button" className="bd-settings-btn-outline" onClick={() => { setShowDeleteConfirm(false); setPasswordForDeletion(''); }}>Cancel</button>
                        <button type="submit" className="bd-settings-btn-danger" disabled={deletingAccount || !passwordForDeletion}>
                          {deletingAccount ? <><FontAwesomeIcon icon={faSpinner} spin /> Deleting…</> : 'Yes, Delete Permanently'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  };

  // Swap deal action handlers
  const handleAcceptSwap = async (swapId) => {
    if (!window.confirm('Are you sure you want to accept this swap offer?')) return;

    try {
      await swapAPI.acceptSwapDeal(swapId);
      setToast({ message: 'Swap deal accepted successfully!', type: 'success' });

      // Refresh swap deals
      const response = await swapAPI.getSwapDeals();
      const acceptP = response?.data || response;
      if (acceptP) {
        const deals = Array.isArray(acceptP) ? acceptP : (acceptP.deals || []);
        setSwapDeals(deals);
      }
    } catch (error) {
      setToast({ message: 'Failed to accept swap deal. Please try again.', type: 'error' });
    }
  };

  const handleRejectSwap = async (swapId) => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');

    try {
      await swapAPI.rejectSwapDeal(swapId, reason);
      setToast({ message: 'Swap deal rejected.', type: 'info' });

      // Refresh swap deals
      const response = await swapAPI.getSwapDeals();
      const rejectP = response?.data || response;
      if (rejectP) {
        const deals = Array.isArray(rejectP) ? rejectP : (rejectP.deals || []);
        setSwapDeals(deals);
      }
    } catch (error) {
      setToast({ message: 'Failed to reject swap deal. Please try again.', type: 'error' });
    }
  };

  const handleCancelSwap = async (swapId) => {
    if (!window.confirm('Are you sure you want to cancel this swap offer?')) return;

    try {
      await swapAPI.cancelSwapDeal(swapId);
      setToast({ message: 'Swap offer cancelled.', type: 'info' });

      // Refresh swap deals
      const response = await swapAPI.getSwapDeals();
      const cancelP = response?.data || response;
      if (cancelP) {
        const deals = Array.isArray(cancelP) ? cancelP : (cancelP.deals || []);
        setSwapDeals(deals);
      }
    } catch (error) {
      setToast({ message: 'Failed to cancel swap deal. Please try again.', type: 'error' });
    }
  };

  const getSwapStatusColor = (status) => {
    const colors = {
      pending: '#FFA726',
      accepted: '#4ECDC4',
      rejected: '#FF6B6B',
      completed: '#22C55E',
      cancelled: '#9CA3AF'
    };
    return colors[status] || '#6B7280';
  };

  const getSwapStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      accepted: 'Accepted',
      rejected: 'Rejected',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return texts[status] || status;
  };

  const renderSwapDealsContent = () => {
    const STATUS_LABEL = {
      pending_seller_review:   { text: 'Awaiting Seller',   color: '#f59e0b' },
      declined_by_seller:      { text: 'Seller Declined',   color: '#ef4444' },
      pending_initiator_review:{ text: 'Review Offer',      color: '#3b82f6' },
      declined_by_initiator:   { text: 'You Declined',      color: '#ef4444' },
      accepted_pending_payment:{ text: 'Pay Now',           color: '#10b981' },
      paid_in_escrow: { text: 'In Escrow', color: '#8b5cf6' },
      shipped:                 { text: 'Shipped',           color: '#0ea5e9' },
      delivered:               { text: 'Delivered',         color: '#22c55e' },
      completed:               { text: 'Completed',         color: '#22c55e' },
      cancelled:               { text: 'Cancelled',         color: '#6b7280' },
    };

    const TERMINAL = ['completed', 'cancelled', 'declined_by_seller', 'declined_by_initiator'];
    const allInitiated = getInitiatedSwaps();
    const activeSwaps  = allInitiated.filter(s => !TERMINAL.includes(s.status));
    const historySwaps = allInitiated.filter(s =>  TERMINAL.includes(s.status));
    const filteredDeals = swapActiveTab === 'active' ? activeSwaps : historySwaps;

    const handleAcceptPrice = async (swapId) => {
      if (!window.confirm('Accept this seller price offer?')) return;
      try {
        await acceptPrice(swapId);
        setToast({ message: 'Price accepted! Proceed to payment.', type: 'success' });
      } catch { setToast({ message: 'Failed to accept price', type: 'error' }); }
    };

    const handleDeclinePrice = async (swapId) => {
      if (!window.confirm('Decline this price offer?')) return;
      try {
        await declinePrice(swapId);
        setToast({ message: 'Price offer declined.', type: 'info' });
      } catch { setToast({ message: 'Failed to decline price', type: 'error' }); }
    };

    const handleCancelOffer = async (swapId) => {
      if (!window.confirm('Cancel this swap offer?')) return;
      try {
        await cancelSwapOffer(swapId);
        setToast({ message: 'Swap offer cancelled.', type: 'info' });
      } catch { setToast({ message: 'Failed to cancel offer', type: 'error' }); }
    };

    const handleConfirmShipment = async (swapId) => {
      if (!window.confirm('Confirm that you have shipped the device?')) return;
      try {
        await confirmShipment(swapId, { tracking_number: 'N/A' });
        setToast({ message: 'Shipment confirmed!', type: 'success' });
      } catch { setToast({ message: 'Failed to confirm shipment', type: 'error' }); }
    };

    const handleConfirmDelivery = async (swapId) => {
      if (!window.confirm('Confirm that you have received the device?')) return;
      try {
        await confirmDelivery(swapId, true);
        setToast({ message: 'Delivery confirmed!', type: 'success' });
      } catch { setToast({ message: 'Failed to confirm delivery', type: 'error' }); }
    };

    const handleCompleteSwap = async (swapId) => {
      if (!window.confirm('Mark this swap as complete? This cannot be undone.')) return;
      try {
        await completeSwap(swapId);
        setToast({ message: 'Swap completed successfully!', type: 'success' });
      } catch { setToast({ message: 'Failed to complete swap', type: 'error' }); }
    };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="bd-settings-card" style={{ marginBottom: '1.5rem' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: isDarkMode ? '#f9fafb' : '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faExchangeAlt} style={{ color: '#22C55E' }} /> My Swap Offers
            </h2>
            <button
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
              onClick={() => navigate('/swap-deals')}
            >
              <FontAwesomeIcon icon={faPlus} /> Browse Swap Items
            </button>
          </div>

          {/* Tabs: Active vs History */}
          <div style={{
            display: 'flex', gap: '0.5rem', marginBottom: '1.5rem',
            background: isDarkMode ? '#1f2937' : '#f3f4f6',
            borderRadius: '12px', padding: '4px'
          }}>
            <button
              onClick={() => setSwapActiveTab('active')}
              style={{
                flex: 1, padding: '0.75rem 1rem',
                background: swapActiveTab === 'active' ? '#22C55E' : 'transparent',
                color: swapActiveTab === 'active' ? 'white' : isDarkMode ? '#9ca3af' : '#6b7280',
                border: 'none', borderRadius: '8px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.2s', position: 'relative', zIndex: 1
              }}
            >
              Active ({activeSwaps.length})
            </button>
            <button
              onClick={() => setSwapActiveTab('history')}
              style={{
                flex: 1, padding: '0.75rem 1rem',
                background: swapActiveTab === 'history' ? '#22C55E' : 'transparent',
                color: swapActiveTab === 'history' ? 'white' : isDarkMode ? '#9ca3af' : '#6b7280',
                border: 'none', borderRadius: '8px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.2s', position: 'relative', zIndex: 1
              }}
            >
              History ({historySwaps.length})
            </button>
          </div>

          <div>
            {swapLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>Loading swap offers...</p>
              </div>
            ) : filteredDeals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                <FontAwesomeIcon icon={faExchangeAlt} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.5 }} />
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: isDarkMode ? '#f3f4f6' : '#1f2937' }}>
                  {swapActiveTab === 'active' ? 'No Active Swap Offers' : 'No Swap History'}
                </h3>
                <p style={{ marginBottom: '1.5rem' }}>
                  {swapActiveTab === 'active'
                    ? "You don't have any active swap offers. Browse items to start a swap."
                    : "Completed, cancelled, or declined offers will appear here."}
                </p>
                {swapActiveTab === 'active' && (
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                    onClick={() => navigate('/swap-deals')}
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} /> Browse Swap Items
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {filteredDeals.map((offer) => {
                  const offerId = offer.id || offer._id;
                  const statusInfo = STATUS_LABEL[offer.status] || { text: offer.status || 'Unknown', color: '#6b7280' };
                  const isSellerOffer = offer.status === 'pending_initiator_review';
                  const isAwaitingSeller = offer.status === 'pending_seller_review';
                  const isPendingPayment = offer.status === 'accepted_pending_payment';
                  const canCancel = ['pending_seller_review', 'pending_initiator_review'].includes(offer.status);

                  // Target listing product (populated by backend or just the ID)
                  const listingProduct = offer.listing_product || offer.product || null;

                  return (
                    <div key={offerId} style={{
                      borderRadius: '14px', padding: '16px 20px',
                      background: isDarkMode ? '#1e293b' : '#f8fafc',
                      border: `1px solid ${isSellerOffer ? '#3b82f6' : isPendingPayment ? '#10b981' : (isDarkMode ? '#334155' : '#e2e8f0')}`,
                      boxShadow: isSellerOffer ? '0 0 0 2px #3b82f622' : isPendingPayment ? '0 0 0 2px #10b98122' : (isDarkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)'),
                      position: 'relative',
                    }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Offer #{offer.swap_number || String(offerId).slice(-8).toUpperCase()}
                          </span>
                          <h4 style={{ margin: '3px 0 2px', fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                            {offer.model}{offer.storage ? ` · ${offer.storage}` : ''}
                          </h4>
                          <span style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                            {offer.device_type} · Condition {offer.condition_rating}/10
                            {offer.battery_health != null ? ` · Battery ${offer.battery_health}%` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span style={{
                            padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                            background: statusInfo.color + '22', color: statusInfo.color, whiteSpace: 'nowrap'
                          }}>
                            {statusInfo.text}
                          </span>
                          <span style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8' }}>
                            {new Date(offer.created_at || offer.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Target listing product (what buyer wants) */}
                      {listingProduct && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 12px', borderRadius: '9px', marginBottom: '10px',
                          background: isDarkMode ? '#0f172a' : '#f8fafc',
                          border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}`
                        }}>
                          {listingProduct.images?.[0]?.url && (
                            <img
                              src={listingProduct.images[0].url}
                              alt={listingProduct.title || listingProduct.name}
                              style={{ width: 44, height: 44, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <div style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8', marginBottom: '2px' }}>Swapping for</div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                              {listingProduct.title || listingProduct.name || 'Listed product'}
                            </div>
                            {listingProduct.price && (
                              <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 700 }}>
                                ₦{Number(listingProduct.price).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Your device's photos */}
                      {offer.images?.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                          {offer.images.slice(0, 5).map((img, i) => (
                            <img key={i} src={img} alt={`Your device ${i + 1}`}
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ))}
                          {offer.images.length > 5 && (
                            <div style={{ width: '48px', height: '48px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#0f172a' : '#f1f5f9', fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b' }}>+{offer.images.length - 5}</div>
                          )}
                        </div>
                      )}

                      {/* Seller's top-up counter-offer */}
                      {isSellerOffer && offer.seller_topup_amount != null && (
                        <div style={{
                          padding: '12px 14px', borderRadius: '9px', marginBottom: '10px',
                          background: isDarkMode ? '#0f172a' : '#eff6ff',
                          border: `1px solid ${isDarkMode ? '#1e40af' : '#bfdbfe'}`,
                        }}>
                          <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#60a5fa' : '#1e40af' }}>
                            Seller's Top-Up Request
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px', marginBottom: '10px' }}>
                            <div>
                              <div style={{ opacity: 0.7, marginBottom: '2px' }}>Top-up Amount</div>
                              <div style={{ fontWeight: 700, fontSize: '15px', color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                                ₦{Number(offer.seller_topup_amount).toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div style={{ opacity: 0.7, marginBottom: '2px' }}>Total You Pay</div>
                              <div style={{ fontWeight: 700, fontSize: '15px', color: isDarkMode ? '#60a5fa' : '#1e40af' }}>
                                ₦{(() => {
                                  try { return calculateSwapFees(offer.seller_topup_amount).total.toLocaleString(); }
                                  catch { return (offer.seller_topup_amount + (offer.platform_fee || 0) + (offer.vat_amount || 0)).toLocaleString(); }
                                })()}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleAcceptPrice(offerId)}
                              style={{ flex: 1, padding: '9px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#10b981', color: '#fff', fontWeight: 600, fontSize: '13px' }}
                            >
                              <FontAwesomeIcon icon={faCheck} style={{ marginRight: '6px' }} />Accept & Pay
                            </button>
                            <button
                              onClick={() => handleDeclinePrice(offerId)}
                              style={{ flex: 1, padding: '9px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: isDarkMode ? '#374151' : '#e5e7eb', color: isDarkMode ? '#f1f5f9' : '#374151', fontWeight: 600, fontSize: '13px' }}
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Post-payment fulfillment actions */}
                      {offer.status === 'paid_in_escrow' && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleConfirmShipment(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: '13px',
                            }}
                          >
                            📦 Mark as Shipped
                          </button>
                        </div>
                      )}

                      {offer.status === 'shipped' && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleConfirmDelivery(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: '13px',
                            }}
                          >
                            ✅ Confirm Delivery
                          </button>
                        </div>
                      )}

                      {offer.status === 'delivered' && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleCompleteSwap(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '13px',
                            }}
                          >
                            🏁 Complete Swap
                          </button>
                        </div>
                      )}

                      {/* Status hint */}
                      <div style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: canCancel ? '10px' : 0, marginTop: '8px' }}>
                        {isAwaitingSeller && '⏳ Waiting for the seller to review your offer...'}
                        {isSellerOffer && !offer.seller_topup_amount && '📢 Seller responded — check the offer details above.'}
                        {offer.status === 'declined_by_seller' && '❌ Seller declined. Try browsing other items or submit a new offer.'}
                        {offer.status === 'declined_by_initiator' && '✗ You declined this seller offer.'}
                        {isPendingPayment && '💳 Offer accepted — proceed to payment to complete the swap.'}
                        {offer.status === 'paid_in_escrow' && '🔒 Payment in escrow — mark as shipped once you send the device.'}
                        {offer.status === 'shipped' && '🚚 Device shipped — confirm once you receive the seller\'s device.'}
{offer.status === 'delivered' && '📬 Both devices delivered — complete the swap.'}
                        {offer.status === 'completed' && '✅ Swap completed successfully!'}
                        {offer.status === 'cancelled' && '🚫 This swap offer was cancelled.'}
                      </div>

                      {/* Cancel button */}
                      {canCancel && (
                        <button
                          onClick={() => handleCancelOffer(offerId)}
                          style={{
                            width: '100%', padding: '8px', borderRadius: '7px',
                            border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`,
                            background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b',
                            fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                          }}
                        >
                          Cancel Offer
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSwapOffersContent = () => {
    const STATUS_LABEL = {
      pending_seller_review: { text: 'Awaiting Seller Response', color: '#f59e0b' },
      declined_by_seller: { text: 'Seller Declined', color: '#ef4444' },
      pending_initiator_review: { text: 'Seller Made Offer', color: '#3b82f6' },
      declined_by_initiator: { text: 'You Declined', color: '#ef4444' },
      accepted_pending_payment: { text: 'Ready for Payment', color: '#10b981' },
      paid_in_escrow: { text: 'In Escrow', color: '#8b5cf6' },
      shipped: { text: 'Shipped', color: '#0ea5e9' },
      delivered: { text: 'Delivered', color: '#22c55e' },
      completed: { text: 'Completed', color: '#22c55e' },
      cancelled: { text: 'Cancelled', color: '#6b7280' },
    };

    const initiatedOffers = getInitiatedSwaps();
    const pendingCount = initiatedOffers.filter(s => s.status === 'pending_initiator_review').length;

    const handleAcceptPrice = async (swapId) => {
      if (!window.confirm('Accept this seller price offer?')) return;
      try {
        await acceptPrice(swapId);
        setToast({ message: 'Price accepted! Proceed to payment below.', type: 'success' });
      } catch (err) {
        setToast({ message: 'Failed to accept price', type: 'error' });
      }
    };

    const handleDeclinePrice = async (swapId) => {
      if (!window.confirm('Decline this price offer? You can submit a new offer.')) return;
      try {
        await declinePrice(swapId);
        setToast({ message: 'Price offer declined.', type: 'info' });
      } catch (err) {
        setToast({ message: 'Failed to decline price', type: 'error' });
      }
    };

    const handlePayNow = async (swapId) => {
      if (!window.confirm('Pay via wallet to complete this swap?')) return;
      try {
        await processSwapPayment(swapId, 'wallet', 2500);
        setToast({ message: 'Payment successful! Swap is now being processed.', type: 'success' });
      } catch (err) {
        setToast({ message: err.message || 'Payment failed', type: 'error' });
      }
    };

    const handleCancelOffer = async (swapId) => {
      if (!window.confirm('Cancel this swap offer?')) return;
      try {
        await cancelSwapOffer(swapId);
        setToast({ message: 'Swap offer cancelled.', type: 'info' });
      } catch (err) {
        setToast({ message: 'Failed to cancel offer', type: 'error' });
      }
    };

    const handleConfirmShipment = async (swapId) => {
      if (!window.confirm('Confirm that you have shipped the device?')) return;
      try {
        await confirmShipment(swapId, { tracking_number: 'N/A' });
        setToast({ message: 'Shipment confirmed!', type: 'success' });
      } catch (err) {
        setToast({ message: 'Failed to confirm shipment', type: 'error' });
      }
    };

    const handleConfirmDelivery = async (swapId) => {
      if (!window.confirm('Confirm that you have received the device?')) return;
      try {
        await confirmDelivery(swapId, true);
        setToast({ message: 'Delivery confirmed!', type: 'success' });
      } catch (err) {
        setToast({ message: 'Failed to confirm delivery', type: 'error' });
      }
    };

    const handleCompleteSwap = async (swapId) => {
      if (!window.confirm('Mark this swap as complete? This cannot be undone.')) return;
      try {
        await completeSwap(swapId);
        setToast({ message: 'Swap completed successfully!', type: 'success' });
      } catch (err) {
        setToast({ message: 'Failed to complete swap', type: 'error' });
      }
    };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="bd-settings-card" style={{ marginBottom: '1.5rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
            <h2 style={{ margin: '0 0 4px', fontSize: '1.25rem', fontWeight: 700, color: isDarkMode ? '#f9fafb' : '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faExchangeAlt} style={{ color: '#22C55E' }} /> My Swap Offers
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
              Track your submitted swap offers and manage seller responses
            </p>
          </div>

          <div>
            {swapLoading && (
              <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>Loading your swap offers...</p>
              </div>
            )}

            {!swapLoading && initiatedOffers.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                <FontAwesomeIcon icon={faExchangeAlt} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.5 }} />
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: isDarkMode ? '#f3f4f6' : '#1f2937' }}>
                  No Swap Offers Yet
                </h3>
                <p style={{ marginBottom: '1.5rem' }}>
                  Start by browsing items available for swap and submit an offer
                </p>
              </div>
            )}

            {!swapLoading && initiatedOffers.length > 0 && (
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {initiatedOffers.map((offer) => {
                  const statusInfo = STATUS_LABEL[offer.status] || { text: offer.status, color: '#6b7280' };
                  const offerId = offer.id || offer._id;
                  const isAwaitingSeller = offer.status === 'pending_seller_review';
                  const isSellerOffer = offer.status === 'pending_initiator_review';

                  return (
                    <div key={offerId} style={{
                      borderRadius: '12px', padding: '16px 20px',
                      background: isDarkMode ? '#1e293b' : '#f8fafc',
                      border: `1px solid ${isSellerOffer ? '#3b82f6' : (isDarkMode ? '#334155' : '#e2e8f0')}`,
                      boxShadow: isSellerOffer ? '0 0 0 2px #3b82f622' : (isDarkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)'),
                      position: 'relative',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase' }}>
                            Offer {offer.swap_number || offerId?.slice(-8)?.toUpperCase()}
                          </span>
                          <h4 style={{ margin: '4px 0 2px', fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                            {offer.model} · {offer.storage}
                          </h4>
                          <span style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                            {offer.device_type} · Condition {offer.condition_rating}/10
                            {offer.battery_health != null ? ` · Battery ${offer.battery_health}%` : ''}
                          </span>
                        </div>
                        <span style={{
                          padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
                          background: statusInfo.color + '22', color: statusInfo.color,
                        }}>
                          {statusInfo.text}
                        </span>
                      </div>

                      {/* Target listing product (what you want) */}
                      {offer.listing_product && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 12px', borderRadius: '9px', marginBottom: '10px',
                          background: isDarkMode ? '#0f172a' : '#f8fafc',
                          border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}`
                        }}>
                          {(offer.listing_product.images?.[0]?.url || offer.listing_product.images?.[0] || offer.listing_product.image) && (
                            <img
                              src={offer.listing_product.images?.[0]?.url || offer.listing_product.images?.[0] || offer.listing_product.image}
                              alt={offer.listing_product.title || offer.listing_product.name}
                              style={{ width: 44, height: 44, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <div style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8', marginBottom: '2px' }}>Swapping for</div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                              {offer.listing_product.title || offer.listing_product.name || 'Listed product'}
                            </div>
                            {offer.listing_product.price && (
                              <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 700 }}>
                                ₦{Number(offer.listing_product.price).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Your device's photos */}
                      {offer.images?.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                          {offer.images.slice(0, 5).map((img, i) => (
                            <img key={i} src={img} alt={`Your device ${i + 1}`}
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ))}
                          {offer.images.length > 5 && (
                            <div style={{ width: '48px', height: '48px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#0f172a' : '#f1f5f9', fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b' }}>+{offer.images.length - 5}</div>
                          )}
                        </div>
                      )}

                      {/* Seller's Price Offer (pending_initiator_review) — show grand total only, no breakdown */}
                      {isSellerOffer && (
                        <div style={{
                          marginTop: '12px', padding: '12px 14px', borderRadius: '8px',
                          background: isDarkMode ? '#0f172a' : '#eff6ff',
                          border: `1px solid ${isDarkMode ? '#1e40af' : '#bfdbfe'}`,
                        }}>
                          <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#60a5fa' : '#1e40af' }}>
                            <FontAwesomeIcon icon={faNairaSign} /> Seller's Price
                          </p>
                          <div style={{ fontWeight: 800, fontSize: '22px', color: isDarkMode ? '#f1f5f9' : '#1e293b', marginBottom: '10px' }}>
                            ₦{Number(offer.total_payable || offer.seller_topup_amount || 0).toLocaleString()}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleAcceptPrice(offerId)}
                              style={{
                                flex: 1, padding: '8px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                                background: '#10b981', color: '#fff', fontWeight: 600, fontSize: '13px',
                              }}
                            >
                              <FontAwesomeIcon icon={faCheck} /> Accept Price
                            </button>
                            <button
                              onClick={() => handleDeclinePrice(offerId)}
                              style={{
                                flex: 1, padding: '8px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                                background: isDarkMode ? '#374151' : '#e5e7eb', color: isDarkMode ? '#f1f5f9' : '#374151', fontWeight: 600, fontSize: '13px',
                              }}
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Pay Now block (accepted_pending_payment) */}
                      {offer.status === 'accepted_pending_payment' && (
                        <div style={{
                          marginTop: '12px', padding: '12px 14px', borderRadius: '8px',
                          background: isDarkMode ? '#052e16' : '#f0fdf4',
                          border: `1px solid ${isDarkMode ? '#166534' : '#bbf7d0'}`,
                        }}>
                          <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#4ade80' : '#166534' }}>
                            💳 Ready to Pay
                          </p>
                          <div style={{ fontWeight: 800, fontSize: '22px', color: isDarkMode ? '#f1f5f9' : '#1e293b', marginBottom: '10px' }}>
                            ₦{Number(offer.total_payable || offer.seller_topup_amount || 0).toLocaleString()}
                            <span style={{ fontSize: '13px', fontWeight: 400, color: isDarkMode ? '#6b7280' : '#9ca3af', marginLeft: 6 }}>+ ₦2,500 logistics</span>
                          </div>
                          <button
                            onClick={() => handlePayNow(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: '14px',
                            }}
                          >
                            <FontAwesomeIcon icon={faWallet} style={{ marginRight: 6 }} /> Pay via Wallet
                          </button>
                        </div>
                      )}

                      {/* Post-payment fulfillment actions */}
                      {offer.status === 'paid_in_escrow' && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleConfirmShipment(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: '13px',
                            }}
                          >
                            📦 Mark as Shipped
                          </button>
                        </div>
                      )}

                      {offer.status === 'shipped' && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleConfirmDelivery(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: '13px',
                            }}
                          >
                            ✅ Confirm Delivery
                          </button>
                        </div>
                      )}

                      {offer.status === 'delivered' && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            onClick={() => handleCompleteSwap(offerId)}
                            style={{
                              width: '100%', padding: '10px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                              background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '13px',
                            }}
                          >
                            🏁 Complete Swap
                          </button>
                        </div>
                      )}

                      {/* Status helper text */}
                      <div style={{ marginTop: '10px', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                        {isAwaitingSeller && '⏳ Waiting for seller to review your offer...'}
                        {isSellerOffer && '📢 Seller has responded — review the price above and accept or decline.'}
                        {offer.status === 'declined_by_seller' && '❌ Seller declined this offer. Try another product or submit a new offer.'}
                        {offer.status === 'declined_by_initiator' && '✗ You declined this offer.'}
                        {offer.status === 'accepted_pending_payment' && '💳 Price accepted — complete payment via wallet to start the swap.'}
                        {offer.status === 'paid_in_escrow' && '🔒 Payment in escrow — mark as shipped once you send the device.'}
                        {offer.status === 'shipped' && '🚚 Device shipped — confirm once you receive the seller\'s device.'}
                        {offer.status === 'delivered' && '📬 Both devices delivered — complete the swap to finalize.'}
                        {offer.status === 'completed' && '✅ Swap completed successfully!'}
                        {offer.status === 'cancelled' && '🚫 This swap has been cancelled.'}
                      </div>

                      {/* Cancel button — only while pending seller review */}
                      {offer.status === 'pending_seller_review' && (
                        <button
                          onClick={() => handleCancelOffer(offerId)}
                          style={{
                            marginTop: '10px', width: '100%', padding: '8px', borderRadius: '7px',
                            border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`,
                            background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b',
                            fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                          }}
                        >
                          Cancel Offer
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEscrowContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faLock} /> Escrow Analytics & Settings
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <EscrowAnalytics />
          <EscrowSettingsPanel />
          <div style={{ textAlign: 'center', padding: '3rem 1rem', marginTop: '2rem' }}>
            <FontAwesomeIcon icon={faLock} size="3x" style={{ color: '#22C55E', marginBottom: '1.5rem' }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>How Escrow Protection Works</h3>
            <p style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
              Your payments are held securely in escrow until you confirm delivery. This protects both buyers and sellers in every transaction.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#22C55E' }} />
                  Place Order
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Order your item and payment goes to escrow</p>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faTruck} style={{ color: '#22C55E' }} />
                  Receive Item
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Seller ships the item to your address</p>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22C55E' }} />
                  Confirm & Release
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Confirm delivery to release payment to seller</p>
              </div>
            </div>
            <div style={{ marginTop: '2rem', padding: '1rem', background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#4ADE80' : '#16A34A', margin: 0 }}>
                <FontAwesomeIcon icon={faShieldAlt} /> All your orders are automatically protected with escrow when enabled at checkout
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWtbRequests = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faClipboardList} /> My "Want to Buy" Requests
          </h2>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowRequestModal(true)}
            style={{ padding: '0.6rem 1.2rem', borderRadius: '8px' }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} /> Post New Request
          </button>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '2rem' }}>
            Tell sellers what you're looking for. When a seller has a matching item, they'll send you a link to their product.
          </p>

          {loadingRequests ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
              <p style={{ marginTop: '1rem' }}>Loading your requests...</p>
            </div>
          ) : wtbRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: '15px', border: '2px dashed #e2e8f0' }}>
              <FontAwesomeIcon icon={faSearch} size="3x" style={{ color: '#cbd5e1', marginBottom: '1.5rem' }} />
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No active requests</h3>
              <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto 1.5rem' }}>
                You haven't posted any "Want to Buy" requests yet.
              </p>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={() => setShowRequestModal(true)}
              >
                Create Your First Request
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
              {wtbRequests.map(req => (
                <div
                  key={req.id || req._id}
                  style={{
                    background: isDarkMode ? '#1e293b' : '#fff',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid',
                    borderColor: req.is_fulfilled ? '#22c55e' : (isDarkMode ? '#334155' : '#e2e8f0'),
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: req.is_fulfilled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                      color: req.is_fulfilled ? '#22c55e' : '#3b82f6'
                    }}>
                      {req.is_fulfilled ? 'Fulfilled' : 'Open'}
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {!req.is_fulfilled && (
                        <button
                          onClick={() => handleMarkRequestFulfilled(req.id || req._id)}
                          title="Mark as Fulfilled"
                          style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', padding: '4px' }}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteRequest(req.id || req._id)}
                        title="Delete Request"
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </div>

                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem', color: isDarkMode ? '#f8fafc' : '#1e293b' }}>
                    {req.title}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '1rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {req.description}
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                      <FontAwesomeIcon icon={faTag} style={{ color: '#22c55e' }} />
                      {req.category || 'Any Category'}
                    </div>
                    {(req.budget_min || req.budget_max) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                        <FontAwesomeIcon icon={faNairaSign} style={{ color: '#22c55e' }} />
                        {req.budget_min ? `₦${req.budget_min.toLocaleString()}` : '0'} - {req.budget_max ? `₦${req.budget_max.toLocaleString()}` : 'Any'}
                      </div>
                    )}
                    {req.location_preference && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#22c55e' }} />
                        {req.location_preference}
                      </div>
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid ' + (isDarkMode ? '#334155' : '#e2e8f0'), paddingTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {req.responses?.length || 0} Responses
                    </div>
                    <button
                      className="AutoboyExpress-dash-btn"
                      onClick={() => fetchRequestResponses(req.id || req._id)}
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', background: '#3b82f6', color: '#fff' }}
                    >
                      View Responses
                    </button>
                  </div>

                  {viewingResponsesFor === (req.id || req._id) && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: isDarkMode ? 'rgba(0,0,0,0.2)' : '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid ' + (isDarkMode ? '#334155' : '#e2e8f0')
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <h4 style={{ fontSize: '0.85rem', fontWeight: 700 }}>Seller Responses</h4>
                        <button onClick={() => setViewingResponsesFor(null)} style={{ background: 'none', border: 'none', fontSize: '0.8rem', color: '#64748b', cursor: 'pointer' }}>Close</button>
                      </div>
                      {requestResponses.length === 0 ? (
                        <p style={{ fontSize: '0.8rem', textAlign: 'center', padding: '1rem', color: '#94a3b8' }}>No responses yet.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {requestResponses.map(resp => (
                            <div key={resp.id || resp._id} style={{ padding: '0.75rem', background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '6px', border: '1px solid ' + (isDarkMode ? '#475569' : '#cbd5e1') }}>
                              <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>{resp.message}</p>
                              <a
                                href={resp.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: '0.8rem', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                              >
                                <FontAwesomeIcon icon={faEye} /> View Product Listing
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showRequestModal && renderRequestModal()}
    </div>
  );

  const renderRequestModal = () => (
    <div className="AutoboyExpress-premium-overlay" style={{ zIndex: 1000 }}>
      <div className="AutoboyExpress-premium-modal" style={{ maxWidth: '600px', width: '95%' }}>
        <div className="AutoboyExpress-premium-header">
          <div className="AutoboyExpress-premium-title">
            <FontAwesomeIcon icon={faClipboardList} className="AutoboyExpress-premium-icon" />
            <h2>Post a "Want to Buy" Request</h2>
          </div>
          <button className="AutoboyExpress-premium-close" onClick={() => setShowRequestModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleCreateRequest} className="AutoboyExpress-premium-content">
          <p style={{ marginBottom: '1.5rem', color: '#6B7280', fontSize: '0.9rem' }}>
            Broadcasting this request will notify all active sellers on the platform.
          </p>

          <div className="AutoboyExpress-dash-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="AutoboyExpress-dash-form-group">
              <label>What are you looking for? *</label>
              <input
                type="text"
                placeholder="e.g. iPhone 15 Pro Max"
                value={newRequest.title}
                onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                required
              />
            </div>
            <div className="AutoboyExpress-dash-form-group">
              <label>Category</label>
              <select
                value={newRequest.category}
                onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #E5E7EB' }}
              >
                <option value="">All Categories</option>
                <option value="Phones">Phones</option>
                <option value="Laptops">Laptops</option>
                <option value="Tablets">Tablets</option>
                <option value="Accessories">Accessories</option>
              </select>
            </div>
          </div>

          <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
            <label>Detailed Description *</label>
            <textarea
              placeholder="Mention specific colors, storage capacity, condition (New/Used), etc."
              value={newRequest.description}
              onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
              required
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #E5E7EB', minHeight: '100px' }}
            />
          </div>

          <div className="AutoboyExpress-dash-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            <div className="AutoboyExpress-dash-form-group">
              <label>Budget Range (Min ₦)</label>
              <input
                type="number"
                placeholder="0"
                value={newRequest.budget_min}
                onChange={(e) => setNewRequest({ ...newRequest, budget_min: e.target.value })}
              />
            </div>
            <div className="AutoboyExpress-dash-form-group">
              <label>Budget Range (Max ₦)</label>
              <input
                type="number"
                placeholder="Any"
                value={newRequest.budget_max}
                onChange={(e) => setNewRequest({ ...newRequest, budget_max: e.target.value })}
              />
            </div>
          </div>

          <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
            <label>Location Preference (Optional)</label>
            <input
              type="text"
              placeholder="e.g. Lagos, Abuja, or Remote"
              value={newRequest.location_preference}
              onChange={(e) => setNewRequest({ ...newRequest, location_preference: e.target.value })}
            />
          </div>

          <div className="AutoboyExpress-dash-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            <div className="AutoboyExpress-dash-form-group">
              <label>Condition Preference</label>
              <select
                value={newRequest.condition_preference}
                onChange={(e) => setNewRequest({ ...newRequest, condition_preference: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #E5E7EB' }}
              >
                <option value="">Any Condition</option>
                <option value="new">Brand New</option>
                <option value="uk_used">UK Used</option>
                <option value="nigeria_used">Nigeria Used</option>
                <option value="good_deal">Good Deal</option>
              </select>
            </div>
            <div className="AutoboyExpress-dash-form-group">
              <label>Urgency</label>
              <select
                value={newRequest.urgency}
                onChange={(e) => setNewRequest({ ...newRequest, urgency: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #E5E7EB' }}
              >
                <option value="">No Preference</option>
                <option value="asap">ASAP</option>
                <option value="within_a_week">Within a Week</option>
                <option value="within_a_month">Within a Month</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>
          </div>

          <div className="AutoboyExpress-premium-actions" style={{ marginTop: '2rem' }}>
            <button type="button" className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-secondary" onClick={() => setShowRequestModal(false)}>
              Cancel
            </button>
            <button type="submit" className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-primary" disabled={submittingRequest}>
              {submittingRequest ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Posting...</>
              ) : (
                <><FontAwesomeIcon icon={faPaperPlane} /> Post Request</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const getContentForSection = () => {
    switch (activeSection) {
      case 'dashboard': return renderDashboardContent();
      case 'kyc':
        return (
          <div className="AutoboyExpress-dash-content-wrapper">
            <KYCVerification
              userType="buyer"
              email={userProfile?.email || userProfile?.profile?.email || ''}
              isDarkMode={isDarkMode}
              onComplete={() => {
                setToast({
                  message: 'KYC verification submitted successfully! We will review your documents.',
                  type: 'success'
                });
                setTimeout(() => setActiveSection('dashboard'), 2000);
              }}
            />
          </div>
        );
      case 'orders': return renderOrdersContent();
      case 'wallet': return renderWalletContent();
      case 'messages': return renderMessagesContent();
      case 'wishlist': return renderWishlistContent();
      case 'addresses':
        return (
          <AddressManagement
            onToast={setToast}
            isDarkMode={isDarkMode}
          />
        );
      case 'swap-deals': return renderSwapDealsContent();
      case 'swap-offers': return renderSwapOffersContent();
      case 'escrow': return renderEscrowContent();
      case 'saved-searches': return renderSavedSearchesContent();
      case 'price-alerts': return renderPriceAlertsContent();
      case 'badges': return renderBadgesContent();
      case 'activity': return renderActivityContent();
      case 'requests': return renderWtbRequests();
      case 'settings': return renderSettingsContent();
      default: return renderDashboardContent();
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="AutoboyExpress-dash-container">
        <div className={`AutoboyExpress-dash-sidebar ${isMobileSidebarOpen ? 'AutoboyExpress-dash-sidebar-open' : ''}`}>
          <div className="AutoboyExpress-dash-logo"><img src={isDarkMode ? darkLogo : lightLogo} alt="AutoboyExpress" /></div>
          <div className="AutoboyExpress-dash-profile">
            <div className="AutoboyExpress-dash-profile-avatar" style={{ overflow: 'hidden', borderRadius: '50%', background: '#1f2937' }}>
              {(avatarPreview || userProfile?.avatar)
                ? <img src={avatarPreview || userProfile.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width:60%;height:60%;fill:#9ca3af;margin:20%"><path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/></svg>'; }} />
                : <FontAwesomeIcon icon={faUser} />}
            </div>
            <div className="AutoboyExpress-dash-profile-info">
              <h3 className="AutoboyExpress-dash-seller-name">
                {userProfile ? `${userProfile.profile?.first_name || ''} ${userProfile.profile?.last_name || ''}`.trim() || 'User' : 'Loading...'}
              </h3>
              <p className="AutoboyExpress-dash-seller-status">Buyer Account</p>
            </div>
          </div>

          <ul className="AutoboyExpress-dash-nav">
            {/* Homepage Link */}
            <li className="AutoboyExpress-dash-nav-item">
              <button className="AutoboyExpress-dash-nav-link" onClick={() => { navigate('/homepage'); setIsMobileSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faHome} className="AutoboyExpress-dash-nav-icon" />
                Homepage
              </button>
            </li>

            {[
              { id: 'dashboard', icon: faChartBar, label: 'Dashboard' },
              { id: 'kyc', icon: faShieldAlt, label: 'KYC Verification' },
              { id: 'orders', icon: faShoppingCart, label: 'My Orders' },
              { id: 'wallet', icon: faWallet, label: 'Wallet' },
              { id: 'wishlist', icon: faHeart, label: 'Wishlist' },
              { id: 'addresses', icon: faMapMarkerAlt, label: 'My Addresses' },
              { id: 'swap-deals', icon: faExchangeAlt, label: 'Swap Deals' },
              { id: 'swap-offers', icon: faBox, label: 'My Swap Offers' },
              { id: 'escrow', icon: faLock, label: 'Escrow' },
              { id: 'saved-searches', icon: faSearch, label: 'Saved Searches' },
              { id: 'price-alerts', icon: faBell, label: 'Price Alerts' },
              { id: 'requests', icon: faClipboardList, label: 'My Requests' },
              { id: 'badges', icon: faStar, label: 'Badges' },
              { id: 'activity', icon: faHistory, label: 'Activity' },
              { id: 'settings', icon: faCog, label: 'Settings' }
            ].map(item => (
              <li key={item.id} className="AutoboyExpress-dash-nav-item">
                <button className={`AutoboyExpress-dash-nav-link ${activeSection === item.id ? 'active' : ''}`} onClick={() => { setActiveSection(item.id); setIsMobileSidebarOpen(false); }}>
                  <FontAwesomeIcon icon={item.icon} className="AutoboyExpress-dash-nav-icon" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {isMobileSidebarOpen && <div className="AutoboyExpress-dash-mobile-overlay" onClick={() => setIsMobileSidebarOpen(false)} />}

        <div className="AutoboyExpress-dash-main">
          <div className="AutoboyExpress-dash-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button className="AutoboyExpress-dash-mobile-toggle" onClick={toggleMobileSidebar}><FontAwesomeIcon icon={faBars} /></button>
              <h1 className="AutoboyExpress-dash-title">{activeSection.charAt(0).toUpperCase() + activeSection.slice(1).replace('-', ' ')}</h1>
            </div>
            <div className="AutoboyExpress-dash-header-actions">
              {/* Notifications */}
              <div className="AutoboyExpress-dash-notification-wrapper">
                <button
                  className="AutoboyExpress-dash-notification-btn"
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowSettings(false);
                  }}
                >
                  <FontAwesomeIcon icon={faBell} />
                  {notifications.filter(n => n.unread).length > 0 && (
                    <span className="AutoboyExpress-dash-notification-badge">
                      {notifications.filter(n => n.unread).length}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-notifications-dropdown">
                    <div className="AutoboyExpress-dash-dropdown-header">
                      <h3>Notifications</h3>
                      <button className="AutoboyExpress-dash-mark-read-btn">Mark all as read</button>
                    </div>
                    <div className="AutoboyExpress-dash-dropdown-content">
                      {notifications.map(notif => (
                        <div key={notif.id} className={`AutoboyExpress-dash-notification-item ${notif.unread ? 'unread' : ''}`}>
                          <div className="AutoboyExpress-dash-notification-icon">
                            <FontAwesomeIcon icon={
                              notif.type === 'shipping' ? faTruck :
                                notif.type === 'alert' ? faBell :
                                  notif.type === 'wishlist' ? faHeart :
                                    faCheckCircle
                            } />
                          </div>
                          <div className="AutoboyExpress-dash-notification-content">
                            <h4>{notif.title}</h4>
                            <p>{notif.message}</p>
                            <span>{notif.time}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="AutoboyExpress-dash-dropdown-footer">
                      <button onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>View All Notifications</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Theme Toggle */}
              <button className="AutoboyExpress-dash-theme-toggle" onClick={toggleTheme}>
                <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
              </button>

              {/* Profile Picture */}
              <div
                className="AutoboyExpress-dash-profile-picture"
                onClick={() => setActiveSection('settings')}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid #22C55E',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                  marginLeft: '15px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <img
                  src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.profile?.first_name || 'U')}+${encodeURIComponent(userProfile?.profile?.last_name || 'ser')}&background=22C55E&color=fff&size=128`}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.profile?.first_name || 'U')}+${encodeURIComponent(userProfile?.profile?.last_name || 'ser')}&background=22C55E&color=fff&size=128`;
                  }}
                />
              </div>

              {/* Settings Dropdown */}
              <div className="AutoboyExpress-dash-settings-wrapper">
                <button
                  className="AutoboyExpress-dash-settings-btn"
                  onClick={() => {
                    setShowSettings(!showSettings);
                    setShowNotifications(false);
                  }}
                >
                  <FontAwesomeIcon icon={faCog} />
                </button>

                {showSettings && (
                  <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-settings-dropdown">
                    <div className="AutoboyExpress-dash-dropdown-content">
                      <button className="AutoboyExpress-dash-dropdown-item" onClick={() => { setActiveSection('settings'); setShowSettings(false); }}>
                        <FontAwesomeIcon icon={faUserCircle} />
                        <span>My Profile</span>
                      </button>
                      <button className="AutoboyExpress-dash-dropdown-item" onClick={() => { setActiveSection('settings'); setShowSettings(false); }}>
                        <FontAwesomeIcon icon={faCog} />
                        <span>Settings</span>
                      </button>
                      <button className="AutoboyExpress-dash-dropdown-item" onClick={() => { setActiveSection('settings'); setShowSettings(false); setToast({ message: 'Manage your 2FA and active sessions below in Settings.', type: 'info' }); }}>
                        <FontAwesomeIcon icon={faShieldAlt} />
                        <span>Privacy & Security</span>
                      </button>
                      <button className="AutoboyExpress-dash-dropdown-item" onClick={() => { setShowSettings(false); navigate('/support'); }}>
                        <FontAwesomeIcon icon={faQuestionCircle} />
                        <span>Help & Support</span>
                      </button>
                      <div className="AutoboyExpress-dash-dropdown-divider"></div>
                      <button className="AutoboyExpress-dash-dropdown-item AutoboyExpress-dash-logout-btn" onClick={handleLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="AutoboyExpress-dash-content-area">{getContentForSection()}</div>
        </div>
      </div>

      {/* Wallet Top-up Modal */}
      {showTopupModal && (
        <div className="AutoboyExpress-premium-overlay">
          <div className="AutoboyExpress-premium-modal" style={{ maxWidth: '400px' }}>
            <div className="AutoboyExpress-premium-header">
              <div className="AutoboyExpress-premium-title">
                <FontAwesomeIcon icon={faWallet} className="AutoboyExpress-premium-icon" />
                <h2>Top Up Wallet</h2>
              </div>
              <button
                className="AutoboyExpress-premium-close"
                onClick={() => setShowTopupModal(false)}
                disabled={isProcessingTopup}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="AutoboyExpress-premium-content">
              <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                Enter the amount you want to add to your wallet. Minimum amount is ₦100.
              </p>
              <div className="AutoboyExpress-dash-form-group">
                <label>Amount (NGN)</label>
                <input
                  type="number"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="100"
                  step="100"
                  disabled={isProcessingTopup}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div className="AutoboyExpress-premium-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-secondary"
                  onClick={() => setShowTopupModal(false)}
                  disabled={isProcessingTopup}
                >
                  Cancel
                </button>
                <button
                  className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-primary"
                  onClick={handleWalletTopup}
                  disabled={isProcessingTopup || !topupAmount}
                >
                  {isProcessingTopup ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faWallet} />
                      <span>Top Up ₦{topupAmount || '0'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bank Accounts Manager */}
      {showBankAccounts && (
        <BankAccountsManager onClose={() => setShowBankAccounts(false)} />
      )}

      {/* Wallet Withdrawal Modal */}
      <WalletWithdrawModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        balance={walletBalance.balance}
        isDarkMode={isDarkMode}
        onSuccess={(amount) => {
          setToast({ message: `✓ Withdrawal of ₦${amount.toLocaleString()} submitted`, type: 'success' });
          fetchWalletBalance();
          fetchWalletTransactions();
        }}
      />

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showPremiumUpgrade}
        onClose={() => setShowPremiumUpgrade(false)}
        userType="buyer"
      />

      {/* Order Tracking Modal */}
      {showTracking && trackingOrderId && (
        <OrderTracking
          orderId={trackingOrderId}
          onClose={() => {
            setShowTracking(false);
            setTrackingOrderId(null);
          }}
        />
      )}
    </>
  );
};

export default BuyerDashboard;
